import React, { useMemo, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Tree,
  Typography,
  Upload,
  message,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { assemblyEstimateService } from '@/services/assemblyEstimate.service';
import { documentService } from '@/services/document.service';
import api from '@/services/api.service';
import { QuickCreateOperationModal } from '@/components/manufacturing/QuickCreateOperationModal';
import type { Operation as MasterOperation } from '@/services/operation.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useOpportunities } from '@/hooks/useOpportunities';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { ItemSelect, formatItemProfileMeta } from '@/components/inventory/ItemSelect';
import type { ItemLike } from '@/components/inventory/ItemSelect';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import operationService from '@/services/operation.service';
import { formatMoney as displayAmount, formatPercent, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';
import { openAssemblyEstimatePrint } from '@/utils/openAssemblyEstimatePrint';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_ASSEMBLY_ESTIMATE } from '@/constants/approval-entity-types';
import { useAuthStore } from '@/store/authStore';
import './AssemblyEstimateBuilderPage.css';

const { Title, Text, Paragraph } = Typography;

type NodeRow = {
  id: string;
  parent_id: string | null;
  node_name: string;
  strategy: string;
  is_phantom: boolean;
  quantity_per_parent: number;
  uom: string | null;
  direct_cost: number;
  estimated_cost: number;
  selling_price: number;
  extended_qty: number;
  discount_amount?: number;
  quotation_id?: string | null;
  description?: string | null;
  material_cost?: number;
  equipment_cost?: number;
  manpower_cost?: number;
  subcontract_cost?: number;
  tooling_cost?: number;
  sequence_number?: number;
  actual_material_cost?: number | null;
  actual_equipment_cost?: number | null;
  actual_manpower_cost?: number | null;
  actual_subcontract_cost?: number | null;
  actual_tooling_cost?: number | null;
  actual_captured_at?: string | null;
};

type Detail = {
  estimate: Record<string, any>;
  nodes: NodeRow[];
  materials: Array<Record<string, any>>;
  operations: Array<Record<string, any>>;
  indirects: Array<Record<string, any>>;
  statements: Array<Record<string, any>>;
  offers: Array<Record<string, any>>;
  escalations?: Array<{
    id: string;
    cost_category: string;
    percent_value: number;
    period_label?: string | null;
    remarks?: string | null;
  }>;
  revisions?: Array<Record<string, any>>;
  stack: Record<string, number> & {
    scrapCreditTotal?: number;
    escalationTotal?: number;
  };
  indirectAmounts?: Record<string, number>;
  readiness: Array<{ code: string; message: string; nodeId?: string }>;
  boundaries: Record<string, string>;
  capabilities?: Record<string, string>;
};

type CompareResult = {
  lines: Array<Record<string, any>>;
  stackDelta?: Record<string, number>;
  left?: Record<string, any>;
  right?: Record<string, any>;
};

type RateCard = {
  id: string;
  grade_code: string;
  grade_name: string;
  hourly_rate: number;
  currency?: string;
};

type ActualCompareRow = {
  key: string;
  category: string;
  estimated: number;
  field: string;
  actual: number | null | undefined;
};

const ESCALATION_CATEGORIES = ['MATERIAL', 'EQUIPMENT', 'MANPOWER', 'SUBCONTRACT', 'TOOLING', 'ALL'] as const;

const INDIRECT_CATEGORY_SUGGESTIONS = [
  'Electricity',
  'Site facilities',
  'Supervision',
  'Temporary works',
  'Transport',
  'Insurance',
  'Bonds / guarantees',
  'Camp / accommodation',
  'Scaffolding',
  'Crane / lifting',
  'Other indirect',
];

const INDIRECT_METHOD_OPTIONS = [
  { value: 'FIXED', label: 'Fixed amount' },
  { value: 'PERCENT_DIRECT', label: '% of direct cost' },
  { value: 'QTY_RATE', label: 'Quantity × rate' },
  { value: 'DURATION_RATE', label: 'Duration × rate' },
  { value: 'MANUAL', label: 'Manual (fixed amount)' },
];

const INDIRECT_SCOPE_OPTIONS = [
  { value: 'ESTIMATE', label: 'Whole estimate' },
  { value: 'TOP_LEVEL', label: 'Top-level assembly' },
];

function indirectMethodHint(method: string | undefined): string {
  switch (method) {
    case 'PERCENT_DIRECT':
      return 'Percent is applied to estimate direct cost (or the selected top-level assembly’s direct cost).';
    case 'QTY_RATE':
      return 'Amount = quantity × rate.';
    case 'DURATION_RATE':
      return 'Amount = duration × rate. Leave duration blank to use the estimate duration.';
    case 'MANUAL':
      return 'Uses the fixed amount. Prefer Fixed amount unless you need custom allocation later.';
    default:
      return 'Enter a fixed project/site indirect amount.';
  }
}

const STATUS_COLORS: Record<string, string> = {
  Draft: 'blue',
  'In Review': 'orange',
  Approved: 'green',
  Rejected: 'red',
  Converted: 'purple',
};

function money(n: unknown) {
  return displayAmount(n, PRINT_PLAIN_AMOUNT_PROFILE);
}

function rateSourceTag(source: string | undefined, isOverride: boolean) {
  if (isOverride) return <Tag color="orange">Override</Tag>;
  const label = source || 'MANUAL';
  const color =
    label === 'STANDARD' || label === 'OPERATION' || label === 'WORKSTATION'
      ? 'blue'
      : label === 'SUPPLIER_QUOTE'
        ? 'green'
        : 'default';
  return <Tag color={color}>{label}</Tag>;
}

function itemRate(item: ItemLike & { average_cost?: number | null }): number | undefined {
  const standard = Number(item.standard_cost);
  if (standard > 0) return standard;
  const average = Number(item.average_cost);
  if (average > 0) return average;
  const last = Number(item.last_purchase_price);
  if (last > 0) return last;
  return undefined;
}

function unwrapList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === 'object') {
    const obj = body as { data?: unknown; rows?: unknown[] };
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (Array.isArray(obj.rows)) return obj.rows as T[];
  }
  return [];
}

const AssemblyEstimateBuilderPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const perms = user?.permissions || [];
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const canEditPerm = isSuperAdmin || perms.includes('growth.assembly_estimation.edit');
  const canApprovePerm = isSuperAdmin || perms.includes('growth.assembly_estimation.approve');
  const canRevisePerm = isSuperAdmin || perms.includes('growth.assembly_estimation.revise');
  const canConvertPerm = isSuperAdmin || perms.includes('growth.assembly_estimation.convert');
  const canDeletePerm = isSuperAdmin || perms.includes('growth.assembly_estimation.delete');
  const isNew = !id || id === 'new';
  const [createForm] = Form.useForm();
  const [headerForm] = Form.useForm();
  const [offerForm] = Form.useForm();
  const [materialForm] = Form.useForm();
  const [operationForm] = Form.useForm();
  const [escalationForm] = Form.useForm();
  const [indirectForm] = Form.useForm();
  const [actualsForm] = Form.useForm();
  const [editMaterialForm] = Form.useForm();
  const [rateCardForm] = Form.useForm();
  const [editingMaterial, setEditingMaterial] = useState<Record<string, any> | null>(null);
  const [editingOperation, setEditingOperation] = useState<Record<string, any> | null>(null);
  const [editingIndirect, setEditingIndirect] = useState<Record<string, any> | null>(null);
  const [editingEscalation, setEditingEscalation] = useState<Record<string, any> | null>(null);
  const [editingOffer, setEditingOffer] = useState<Record<string, any> | null>(null);
  const [editingRateCard, setEditingRateCard] = useState<RateCard | null>(null);
  const [addMaterialOpen, setAddMaterialOpen] = useState(false);
  const [operationModalOpen, setOperationModalOpen] = useState(false);
  const [quickCreateOperationOpen, setQuickCreateOperationOpen] = useState(false);
  const [operationSelectSearch, setOperationSelectSearch] = useState('');
  const queryClient = useQueryClient();
  const [indirectModalOpen, setIndirectModalOpen] = useState(false);
  const [escalationModalOpen, setEscalationModalOpen] = useState(false);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [rateCardModalOpen, setRateCardModalOpen] = useState(false);
  const [showAdvancedMaterialCols, setShowAdvancedMaterialCols] = useState(false);
  const [showAdvancedOperationCols, setShowAdvancedOperationCols] = useState(false);
  const [nodeWorkspaceTab, setNodeWorkspaceTab] = useState('overview');
  const customerIdWatch = Form.useWatch('customer_id', createForm);
  const offerTargetType = Form.useWatch('target_type', offerForm) || 'MATERIAL';
  const indirectMethodWatch = Form.useWatch('method', indirectForm) || 'FIXED';
  const indirectScopeWatch = Form.useWatch('scope', indirectForm) || 'ESTIMATE';
  const operationOutsourcedWatch = Form.useWatch('is_outsourced', operationForm);
  const { data: customers = [] } = useActiveCustomers();
  const { data: openOpportunities = [] } = useOpportunities({ status: 'Open' });
  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: () => listAssignableSalesTeams(),
    enabled: isNew,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['procurement-suppliers-active'],
    enabled: !isNew,
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers', { params: { only_active: true, limit: 200 } });
      const body = (res as { data?: unknown })?.data ?? res;
      return unwrapList<{ id: string; supplier_name?: string; name?: string }>(body);
    },
  });
  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as { data?: unknown })?.data ?? res;
      return unwrapList<{ id: string; service_code: string; service_name: string }>(body);
    },
  });
  const rateCardsQuery = useQuery({
    queryKey: ['assembly-estimate-rate-cards'],
    enabled: !isNew,
    queryFn: async () => {
      const res = await assemblyEstimateService.rateCards();
      const body = (res as { data?: unknown })?.data ?? res;
      return unwrapList<RateCard>(body);
    },
  });
  const operationsMasterQuery = useQuery({
    queryKey: ['manufacturing-operations-master', 'assembly-estimate'],
    enabled: !isNew,
    queryFn: async () => {
      let companyId = '';
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        companyId = user.company_id || '';
      } catch {
        /* ignore */
      }
      return operationService.getAll(companyId, true);
    },
  });
  const masterOperations = operationsMasterQuery.data || [];
  const { data: supplierCategoriesForOps = [], isLoading: loadingSupplierCategories } = useQuery({
    queryKey: ['procurement-supplier-categories', 'assembly-estimate'],
    enabled: !isNew,
    queryFn: async () => {
      const res = await api.get('/procurement/supplier-categories');
      const body = (res as { data?: unknown })?.data ?? res;
      return unwrapList<{ id: string; category_name?: string; name?: string }>(body);
    },
  });
  const supplierCategorySelectOptions = useMemo(
    () =>
      supplierCategoriesForOps.map((c) => ({
        value: c.id,
        label: c.category_name || c.name || c.id,
      })),
    [supplierCategoriesForOps]
  );
  const [selectedId, setSelectedId] = useState<string>();
  const [mainTab, setMainTab] = useState('assemblies');
  const [moveOpen, setMoveOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertSelectedIds, setConvertSelectedIds] = useState<string[]>([]);
  const [customerSummaryOpen, setCustomerSummaryOpen] = useState(false);
  const [customerSummaryText, setCustomerSummaryText] = useState('');
  const [offerAttachment, setOfferAttachment] = useState<File | null>(null);
  const [compareRight, setCompareRight] = useState('');
  const [compareResult, setCompareResult] = useState<CompareResult | null>(null);

  const detailQuery = useQuery({
    queryKey: ['assembly-estimate', id],
    enabled: !isNew,
    queryFn: async () => (await assemblyEstimateService.get(id!)).data as Detail,
  });
  const templatesQuery = useQuery({
    queryKey: ['assembly-templates'],
    queryFn: async () => (await assemblyEstimateService.templates()).data as Array<Record<string, any>>,
  });
  const bomsQuery = useQuery({
    queryKey: ['mfg-boms-for-assembly'],
    enabled: !isNew,
    queryFn: async () => {
      const res = await api.get('/manufacturing/boms');
      const data = res.data;
      return (Array.isArray(data) ? data : data?.rows || data?.items || []) as Array<Record<string, any>>;
    },
  });

  const detail = detailQuery.data;
  const estimate = detail?.estimate;
  const readOnly = Boolean(
    !canEditPerm ||
      estimate?.is_frozen ||
      ['In Review', 'Approved', 'Converted'].includes(estimate?.status)
  );
  const selected = detail?.nodes.find((n) => n.id === selectedId) || detail?.nodes.find((n) => !n.parent_id);
  const childIds = useMemo(() => new Set((detail?.nodes || []).map((n) => n.parent_id).filter(Boolean)), [detail]);
  const selectedIsLeaf = selected ? !childIds.has(selected.id) : false;
  const canCostSelected = Boolean(selected) && !readOnly;
  const hasNodes = Boolean(detail?.nodes?.length);

  const treeData = useMemo(() => {
    const nodes = detail?.nodes || [];
    const readinessByNode = new Map<string, number>();
    for (const issue of detail?.readiness || []) {
      if (!issue.nodeId) continue;
      readinessByNode.set(issue.nodeId, (readinessByNode.get(issue.nodeId) || 0) + 1);
    }
    const hasChildren = new Set(nodes.map((n) => n.parent_id).filter(Boolean) as string[]);
    const build = (parentId: string | null): any[] =>
      nodes
        .filter((n) => (n.parent_id || null) === parentId)
        .map((n) => {
          const issueCount = readinessByNode.get(n.id) || 0;
          const isLeaf = !hasChildren.has(n.id);
          const isQuote = !n.parent_id && !n.is_phantom;
          const roleLabel = isQuote ? 'Quote line' : isLeaf ? 'Leaf' : 'Parent';
          return {
            key: n.id,
            title: (
              <div
                className="abe-tree-node"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%',
                  minWidth: 0,
                  padding: '2px 0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  {issueCount > 0 ? (
                    <Tooltip title={`${issueCount} readiness issue${issueCount === 1 ? '' : 's'} — open Materials to fix`}>
                      <WarningOutlined style={{ color: '#d48806', fontSize: 13, flexShrink: 0 }} />
                    </Tooltip>
                  ) : null}
                  <Tooltip title={n.node_name}>
                    <Text
                      strong={n.id === selected?.id}
                      ellipsis
                      style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}
                    >
                      {n.node_name}
                    </Text>
                  </Tooltip>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    paddingLeft: issueCount > 0 ? 19 : 0,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                    {n.strategy} · {roleLabel}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, flexShrink: 0, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
                  >
                    {money(n.direct_cost)}
                  </Text>
                </div>
              </div>
            ),
            children: build(n.id),
          };
        });
    return build(null);
  }, [detail, selected?.id]);

  const reload = () => detailQuery.refetch();
  const applyDetailResponse = (res: unknown) => {
    if (!id || !res || typeof res !== 'object') return false;
    const body = res as { data?: Detail; estimate?: Detail['estimate'] };
    const next =
      body.data && typeof body.data === 'object' && 'estimate' in body.data
        ? body.data
        : 'estimate' in body && body.estimate
          ? (body as Detail)
          : null;
    if (!next?.estimate) return false;
    queryClient.setQueryData(['assembly-estimate', id], next);
    return true;
  };
  const run = async (work: () => Promise<unknown>, ok: string) => {
    try {
      const res = await work();
      if (!applyDetailResponse(res)) await reload();
      message.success(ok);
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || 'Request failed');
    }
  };

  const confirmDeleteNode = () => {
    if (!selected || !estimate) return;
    const hasResources =
      detail!.materials.some((m) => m.node_id === selected.id) ||
      detail!.operations.some((o) => o.node_id === selected.id) ||
      detail!.nodes.some(
        (n) =>
          n.parent_id === selected.id &&
          (detail!.materials.some((m) => m.node_id === n.id) || detail!.operations.some((o) => o.node_id === n.id))
      );
    if (hasResources) {
      Modal.warning({
        title: 'Move resources first',
        content:
          'This assembly or its children still have materials or operations. Move them onto another leaf before deleting.',
      });
      setMoveOpen(true);
      return;
    }
    Modal.confirm({
      title: `Delete ${selected.node_name}?`,
      onOk: () => run(() => assemblyEstimateService.deleteNode(estimate.id, selected.id), 'Removed'),
    });
  };

  const openConvertModal = () => {
    if (!detail) return;
    const eligible = detail.nodes.filter((n) => !n.parent_id && !n.is_phantom && !n.quotation_id);
    setConvertSelectedIds(eligible.map((n) => n.id));
    setConvertOpen(true);
  };

  const confirmDeleteEstimate = () => {
    if (!estimate) return;
    Modal.confirm({
      title: 'Delete assembly estimate?',
      content: `Delete "${estimate.estimate_number} — ${estimate.estimate_name}"? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () =>
        run(async () => {
          await assemblyEstimateService.remove(estimate.id);
          navigate('/growth/estimations/assembly');
        }, 'Estimate deleted'),
    });
  };

  const confirmSubmitForReview = () => {
    if (!estimate || !detail) return;
    const readyCount = detail.readiness.length;
    Modal.confirm({
      title: 'Submit for Review',
      content:
        readyCount > 0
          ? `Are you sure you want to submit "${estimate.estimate_name}" for review? There are still ${readyCount} readiness issue${readyCount === 1 ? '' : 's'}.`
          : `Are you sure you want to submit "${estimate.estimate_name}" for review?`,
      onOk: () => run(() => assemblyEstimateService.submit(estimate.id), 'Submitted'),
    });
  };

  const confirmReject = () => {
    if (!estimate) return;
    let reason = '';
    Modal.confirm({
      title: 'Reject estimate',
      content: (
        <div>
          <p>Are you sure you want to reject &quot;{estimate.estimate_name}&quot;?</p>
          <Input.TextArea
            placeholder="Reason for rejection"
            rows={3}
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      onOk: () => run(() => assemblyEstimateService.reject(estimate.id, reason || 'Rejected'), 'Returned to draft'),
    });
  };

  const openReadinessNode = (nodeId?: string) => {
    if (!nodeId) return;
    setSelectedId(nodeId);
    setMainTab('assemblies');
    setNodeWorkspaceTab('materials');
  };

  if (isNew) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/growth/estimations/assembly')}
            style={{ marginBottom: 16 }}
          >
            Back to List
          </Button>
          <Title level={2} style={{ marginTop: 0 }}>New assembly estimate</Title>
          <Paragraph type="secondary">
            Customer, project, department, revenue service, and sales team are required. You will build the assembly tree after create.
          </Paragraph>
          <Form
            form={createForm}
            layout="vertical"
            onFinish={(values) =>
              run(async () => {
                const created = await assemblyEstimateService.create(values);
                navigate(`/growth/estimations/assembly/${created.data.estimate.id}`);
              }, 'Estimate created')
            }
          >
            <Title level={5}>Identity</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="estimate_name" label="Estimate name" rules={[{ required: true }]}>
                  <Input placeholder="e.g. Skid package estimate" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="opportunity_id" label="Opportunity">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Optional"
                    options={openOpportunities.map((o) => ({
                      value: o.id,
                      label: `${o.opportunity_number} — ${o.opportunity_name}`,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Title level={5}>Commercial context</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="customer_id" label="Customer" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select customer"
                    options={customers.map((c: { id: string; customer_name: string }) => ({
                      value: c.id,
                      label: c.customer_name,
                    }))}
                    onChange={(value) => {
                      const customer = customers.find((c: { id: string }) => c.id === value);
                      createForm.setFieldValue('customer_name', customer?.customer_name || '');
                    }}
                  />
                </Form.Item>
                <Form.Item name="customer_name" hidden rules={[{ required: true, message: 'Customer is required' }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="project_id" label="Project" rules={[{ required: true, message: 'Project is required' }]}>
                  <ProjectSelector
                    customerId={customerIdWatch}
                    customerName={customers.find((c: { id: string }) => c.id === customerIdWatch)?.customer_name}
                    allowClear={false}
                    onChange={(projectId, projectData) => {
                      createForm.setFieldsValue({
                        project_id: projectId,
                        project_name: projectData?.project_name,
                      });
                    }}
                  />
                </Form.Item>
                <Form.Item name="project_name" hidden><Input /></Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="department_id" label="Department" rules={[{ required: true, message: 'Department is required' }]}>
                  <DepartmentSelect />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="revenue_service_type_id"
                  label="Revenue service type"
                  rules={[{ required: true, message: 'Revenue service type is required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select revenue service type"
                    options={revenueServiceTypes.map((r) => ({
                      value: r.id,
                      label: `${r.service_code} — ${r.service_name}`,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sales_team_id" label="Sales team" rules={[{ required: true, message: 'Sales team is required' }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select sales team"
                    options={salesTeams.map((t) => ({
                      value: t.id,
                      label: `${t.team_code} — ${t.team_name}`,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Title level={5}>Pricing defaults</Title>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="currency" label="Currency" initialValue="AED">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="pricing_method" label="Pricing" initialValue="MARKUP">
                  <Select
                    options={[
                      { value: 'MARKUP', label: 'Markup on cost' },
                      { value: 'MARGIN', label: 'Margin on selling price' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Create
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    );
  }

  if (detailQuery.isLoading || !detail || !estimate) {
    return (
      <div style={{ padding: 24 }}>
        <Card loading={detailQuery.isLoading}>
          {detailQuery.isError ? <Alert type="error" message="Estimate not found" /> : null}
        </Card>
      </div>
    );
  }

  const materials = selected ? detail.materials.filter((m) => m.node_id === selected.id) : [];
  const operations = selected ? detail.operations.filter((o) => o.node_id === selected.id) : [];
  const quoteNodes = detail.nodes.filter((n) => !n.parent_id && !n.is_phantom);
  const convertCandidates = quoteNodes.filter((n) => !n.quotation_id);
  const selectedReadiness = selected ? detail.readiness.filter((issue) => issue.nodeId === selected.id) : [];
  const marginPct =
    Number(detail.stack.sellingPrice || 0) > 0
      ? ((Number(detail.stack.sellingPrice) - Number(detail.stack.estimatedCost || 0)) / Number(detail.stack.sellingPrice)) * 100
      : 0;
  const scrapCreditTotal = detail.stack.scrapCreditTotal ?? estimate.scrap_credit_total ?? 0;
  const escalationTotal = detail.stack.escalationTotal ?? estimate.escalation_total ?? 0;
  const boundaryEntries = Object.entries(detail.capabilities || detail.boundaries || {});
  const offerTargetOptions =
    offerTargetType === 'OPERATION'
      ? detail.operations.map((o) => ({
          value: o.id,
          label: `${o.description || o.id} · ${money(o.labor_rate)}`,
        }))
      : detail.materials.map((m) => ({
          value: m.id,
          label: `${m.description || m.id} · ${money(m.rate)}`,
        }));

  const resolveOfferTargetLabel = (offer: Record<string, any>) => {
    if (offer.target_type === 'OPERATION') {
      const op = detail.operations.find((o) => o.id === offer.target_id);
      return op?.description || offer.target_id;
    }
    const mat = detail.materials.find((m) => m.id === offer.target_id);
    return mat?.description || offer.target_id;
  };

  const openOfferModal = (row?: Record<string, any>) => {
    setEditingOffer(row || null);
    setOfferAttachment(null);
    setOfferModalOpen(true);
    offerForm.setFieldsValue(
      row
        ? {
            target_type: row.target_type || 'MATERIAL',
            target_id: row.target_id,
            supplier_id: row.supplier_id || undefined,
            supplier_name: row.supplier_name || undefined,
            amount: Number(row.amount ?? 0),
            currency: row.currency || estimate.currency || 'AED',
            note: row.note || undefined,
          }
        : {
            target_type: 'MATERIAL',
            target_id: undefined,
            supplier_id: undefined,
            supplier_name: undefined,
            amount: 0,
            currency: estimate.currency || 'AED',
            note: undefined,
          }
    );
  };

  const submitOffer = async () => {
    const v = await offerForm.validateFields();
    let attachment_document_id: string | undefined = editingOffer?.attachment_document_id || undefined;
    let attachment_url: string | undefined = editingOffer?.attachment_url || undefined;
    if (offerAttachment) {
      const doc = await documentService.uploadDocument(offerAttachment, {
        entity_type: 'assembly_estimate',
        entity_id: estimate.id,
        document_name: offerAttachment.name,
      });
      attachment_document_id = doc.id;
      attachment_url = doc.file_url || doc.file_path;
    }
    const body = { ...v, attachment_document_id, attachment_url };
    let res: unknown;
    if (editingOffer?.id) {
      res = await assemblyEstimateService.updateOffer(estimate.id, editingOffer.id, body);
    } else {
      res = await assemblyEstimateService.addOffer(estimate.id, body);
    }
    message.success(editingOffer ? 'Offer updated' : 'Offer added');
    setOfferModalOpen(false);
    setEditingOffer(null);
    setOfferAttachment(null);
    offerForm.resetFields();
    if (!applyDetailResponse(res)) await reload();
  };

  const openRateCardModal = (row?: RateCard) => {
    setEditingRateCard(row || null);
    setRateCardModalOpen(true);
    rateCardForm.setFieldsValue(
      row
        ? {
            grade_code: row.grade_code,
            grade_name: row.grade_name,
            hourly_rate: Number(row.hourly_rate ?? 0),
            currency: row.currency || estimate.currency || 'AED',
          }
        : {
            grade_code: undefined,
            grade_name: undefined,
            hourly_rate: 0,
            currency: estimate.currency || 'AED',
          }
    );
  };

  const submitRateCard = async () => {
    const v = await rateCardForm.validateFields();
    await assemblyEstimateService.saveRateCard(v, editingRateCard?.id);
    message.success(editingRateCard ? 'Rate card updated' : 'Rate card saved');
    setRateCardModalOpen(false);
    setEditingRateCard(null);
    rateCardForm.resetFields();
    await rateCardsQuery.refetch();
  };

  const canDeleteEstimate =
    canDeletePerm && (estimate.status === 'Draft' || estimate.status === 'Rejected');
  const canSubmit = canApprovePerm && (estimate.status === 'Draft' || estimate.status === 'Rejected');
  const canApproveAction = canApprovePerm && estimate.status === 'In Review';
  const canRevise =
    canRevisePerm && ['Approved', 'Converted', 'Rejected'].includes(estimate.status);
  const canConvertAction = canConvertPerm && estimate.status === 'Approved';
  const statusColor = STATUS_COLORS[estimate.status] || 'default';
  const subtitleParts = [
    `Rev ${estimate.revision_number}`,
    estimate.customer_name,
    estimate.project_name,
    estimate.sales_team_name,
    estimate.cost_basis_date ? `Cost basis ${estimate.cost_basis_date}` : null,
  ].filter(Boolean);

  const moreMenuItems: MenuProps['items'] = [
    {
      key: 'template',
      icon: <DownloadOutlined />,
      label: 'Download import template',
      onClick: () =>
        assemblyEstimateService
          .downloadImportTemplate()
          .then(() => message.success('Template downloaded'))
          .catch((error: any) => message.error(error?.response?.data?.message || error?.message || 'Download failed')),
    },
    {
      key: 'export',
      icon: <DownloadOutlined />,
      label: 'Export Excel',
      onClick: () =>
        assemblyEstimateService
          .downloadExport(estimate.id, estimate.estimate_number)
          .then(() => message.success('Exported'))
          .catch((error: any) => message.error(error?.response?.data?.message || error?.message || 'Export failed')),
    },
    ...(!readOnly
      ? [
          {
            key: 'import',
            icon: <UploadOutlined />,
            label: (
              <Upload
                showUploadList={false}
                accept=".xlsx,.xls"
                beforeUpload={async (file) => {
                  try {
                    const preview = await assemblyEstimateService.previewImport(estimate.id, file);
                    const errors = preview?.data?.errors || [];
                    const warnings = preview?.data?.warnings || [];
                    if (errors.length) {
                      message.error(errors.slice(0, 3).join(' · '));
                      return false;
                    }
                    Modal.confirm({
                      title: `Import ${preview?.data?.rowCount || 0} rows?`,
                      content: (
                        <div>
                          <p>The assembly tree must be empty. Import replaces an empty estimate with the workbook structure.</p>
                          {warnings.length ? (
                            <>
                              <p>Warnings:</p>
                              <ul>{warnings.slice(0, 5).map((w: string) => <li key={w}>{w}</li>)}</ul>
                            </>
                          ) : null}
                        </div>
                      ),
                      onOk: () => run(() => assemblyEstimateService.confirmImport(estimate.id, file), 'Imported'),
                    });
                  } catch (error: any) {
                    message.error(error?.response?.data?.message || error?.message || 'Preview failed');
                  }
                  return false;
                }}
              >
                <span>Import Excel</span>
              </Upload>
            ),
          } as NonNullable<MenuProps['items']>[number],
        ]
      : []),
    {
      key: 'copy',
      icon: <CopyOutlined />,
      label: 'Copy',
      onClick: () =>
        run(
          () =>
            assemblyEstimateService.copy(estimate.id).then((res) =>
              navigate(`/growth/estimations/assembly/${res.data.estimate.id}`)
            ),
          'Copied'
        ),
    },
    ...(canRevise
      ? [
          {
            key: 'revise',
            label: 'New revision',
            onClick: () =>
              run(
                () =>
                  assemblyEstimateService.revise(estimate.id).then((res) =>
                    navigate(`/growth/estimations/assembly/${res.data.estimate.id}`)
                  ),
                'Revision created'
              ),
          } as NonNullable<MenuProps['items']>[number],
        ]
      : []),
    ...(canDeleteEstimate
      ? [
          { type: 'divider' as const },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            onClick: confirmDeleteEstimate,
          } as NonNullable<MenuProps['items']>[number],
        ]
      : []),
  ];

  const headerActions = (
    <Space wrap>
      {canSubmit && (
        <Button type="primary" onClick={confirmSubmitForReview}>
          Submit for Review
        </Button>
      )}
      {canApproveAction && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={() => {
            Modal.confirm({
              title: 'Approve assembly estimate?',
              content: `Approve "${estimate.estimate_number}"? This freezes the revision for conversion.`,
              okText: 'Approve',
              onOk: () => run(() => assemblyEstimateService.approve(estimate.id), 'Approved'),
            });
          }}
        >
          Approve
        </Button>
      )}
      {canApproveAction && (
        <Button danger icon={<CloseCircleOutlined />} onClick={confirmReject}>
          Reject
        </Button>
      )}
      {canConvertAction && (
        <Button type="primary" onClick={openConvertModal}>
          Convert to quotation
        </Button>
      )}
      <Button icon={<PrinterOutlined />} onClick={() => openAssemblyEstimatePrint(estimate.id)}>
        Print
      </Button>
      <ReportDocumentActions
        reportKey="assembly-estimate"
        params={{ id: estimate.id }}
        documentId={estimate.id}
        sendPermissions={['growth.assembly_estimation.edit']}
        emailLabel="Email customer"
      />
      <Dropdown menu={{ items: moreMenuItems }} trigger={['click']}>
        <Button icon={<MoreOutlined />}>More</Button>
      </Dropdown>
    </Space>
  );

  const stackLooksEmpty =
    Number(detail.stack.estimatedCost || 0) === 0 && Number(detail.stack.sellingPrice || 0) === 0;
  const costBuildupChip = (label: string, value: unknown) => (
    <span
      key={label}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 4,
        background: 'rgba(0,0,0,0.04)',
        fontSize: 12,
      }}
    >
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      <Text style={{ fontSize: 12 }}>{money(value)}</Text>
    </span>
  );

  const costSummaryCard = (
    <Card title="Cost summary" size="small">
      <Row gutter={[24, 12]} align="middle">
        <Col flex="auto">
          <Space size={[8, 8]} wrap>
            {costBuildupChip('Direct', detail.stack.directCost)}
            {costBuildupChip('Indirect', detail.stack.indirectCost)}
            {costBuildupChip('Contingency', detail.stack.contingencyCost)}
            {costBuildupChip('Overhead', detail.stack.corporateOverhead)}
            {costBuildupChip('Scrap credit', scrapCreditTotal)}
            {costBuildupChip('Escalation', escalationTotal)}
          </Space>
          {stackLooksEmpty && detail.readiness.length > 0 ? (
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              Add rates on materials/operations to build cost.
            </Text>
          ) : null}
        </Col>
        <Col>
          <Space size={32} wrap>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Estimated
              </Text>
              <Text strong style={{ fontSize: 22 }}>
                {money(detail.stack.estimatedCost)}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Selling
              </Text>
              <Text strong style={{ fontSize: 22, color: '#52c41a' }}>
                {money(detail.stack.sellingPrice)}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Margin %
              </Text>
              <Text strong style={{ fontSize: 18 }}>
                {formatPercent(marginPct, { maximumFractionDigits: 1 })}
              </Text>
            </div>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  const readinessBanner =
    detail.readiness.length > 0 ? (
      <Alert
        type="warning"
        showIcon
        message={`${detail.readiness.length} readiness issue${detail.readiness.length === 1 ? '' : 's'}`}
        description={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {detail.readiness.slice(0, 3).map((issue, index) => (
              <div key={`${issue.code}-${index}`}>
                <Text>{issue.message}</Text>
                {issue.nodeId ? (
                  <Button type="link" size="small" onClick={() => openReadinessNode(issue.nodeId)}>
                    Open
                  </Button>
                ) : null}
              </div>
            ))}
            {detail.readiness.length > 3 ? (
              <Text type="secondary">…and {detail.readiness.length - 3} more</Text>
            ) : null}
          </Space>
        }
      />
    ) : null;

  const assembliesEmpty = (
    <Card>
      <Alert
        type="info"
        showIcon
        message="Add a top-level assembly or seed the tree from a manufacturing BOM."
        style={{ marginBottom: 16 }}
      />
      {!readOnly && (
        <Space direction="vertical">
          <Button
            type="primary"
            onClick={() =>
              run(
                () =>
                  assemblyEstimateService.addNode(estimate.id, {
                    node_name: 'New assembly',
                    quantity_per_parent: 1,
                    uom: 'EA',
                    strategy: 'MAKE',
                  }),
                'Assembly added'
              )
            }
          >
            Add top-level assembly
          </Button>
          <Form
            layout="inline"
            onFinish={(v) =>
              run(() => assemblyEstimateService.seedBom(estimate.id, v.bom_id, Number(v.quantity || 1)), 'BOM seeded')
            }
          >
            <Form.Item name="bom_id" rules={[{ required: true }]}>
              <Select
                showSearch
                placeholder="Manufacturing BOM"
                style={{ width: 280 }}
                options={(bomsQuery.data || []).map((b) => ({
                  value: b.id,
                  label: b.bom_number || b.bom_name || b.id,
                }))}
              />
            </Form.Item>
            <Form.Item name="quantity" initialValue={1}>
              <InputNumber min={0.0001} />
            </Form.Item>
            <Button htmlType="submit">Seed from BOM</Button>
          </Form>
        </Space>
      )}
    </Card>
  );

  const openAddMaterialModal = () => {
    setAddMaterialOpen(true);
  };

  const openAddOperationModal = () => {
    setEditingOperation(null);
    operationForm.resetFields();
    operationForm.setFieldsValue({
      operation_id: undefined,
      description: undefined,
      sequence_number: (operations?.length || 0) + 1,
      setup_hours: 0,
      run_hours_per_unit: 0,
      crew_size: 1,
      labor_rate: 0,
      machine_rate: 0,
      tooling_cost: 0,
      is_outsourced: false,
      outsource_cost_per_unit: 0,
      outsourced_supplier_category_ids: [],
      outsourced_scope_detail: undefined,
      source_currency: estimate.currency || 'AED',
      productivity_units_per_hour: undefined,
      labor_grade_code: undefined,
      remarks: undefined,
      override_reason: undefined,
    });
    setOperationModalOpen(true);
  };

  const openEditOperationModal = (row: Record<string, any>) => {
    setEditingOperation(row);
    operationForm.setFieldsValue({
      operation_id: row.operation_id || undefined,
      description: row.description,
      sequence_number: Number(row.sequence_number ?? 1),
      setup_hours: Number(row.setup_hours ?? 0),
      run_hours_per_unit: Number(row.run_hours_per_unit ?? 0),
      productivity_units_per_hour: row.productivity_units_per_hour,
      crew_size: Number(row.crew_size ?? 1),
      labor_grade_code: row.labor_grade_code || undefined,
      labor_rate: Number(row.labor_rate ?? 0),
      machine_rate: Number(row.machine_rate ?? 0),
      tooling_cost: Number(row.tooling_cost ?? 0),
      source_currency: row.source_currency || estimate.currency || 'AED',
      is_outsourced: Boolean(row.is_outsourced),
      outsource_cost_per_unit: Number(row.outsource_cost_per_unit ?? 0),
      outsourced_supplier_category_ids: row.outsourced_supplier_category_ids || [],
      outsourced_scope_detail: row.outsourced_scope_detail || undefined,
      remarks: row.remarks || undefined,
      override_reason: row.override_reason || undefined,
    });
    setOperationModalOpen(true);
  };

  const submitAddMaterial = async () => {
    const v = await materialForm.validateFields();
    if (!selected) throw new Error('Select an assembly first');
    const rate = Number(v.rate ?? 0);
    const res = await assemblyEstimateService.addMaterial(estimate.id, selected.id, {
      ...v,
      rate,
      source_rate: rate,
      rate_source: 'MANUAL',
      is_override: Boolean(v.override_reason),
    });
    message.success('Material added');
    setAddMaterialOpen(false);
    materialForm.resetFields();
    if (!applyDetailResponse(res)) await reload();
  };

  const submitOperationModal = async () => {
    const v = await operationForm.validateFields();
    if (!selected && !editingOperation) throw new Error('Select an assembly first');
    const isOutsourced = Boolean(v.is_outsourced);
    const description =
      v.description && String(v.description).trim()
        ? String(v.description).trim()
        : isOutsourced
          ? 'Operation outsourced'
          : 'Operation';
    const payload: Record<string, unknown> = {
      operation_id: v.operation_id || null,
      description,
      sequence_number: v.sequence_number || 1,
      is_outsourced: isOutsourced,
      remarks: v.remarks || null,
      source_currency: v.source_currency || estimate.currency || 'AED',
    };
    if (isOutsourced) {
      payload.outsource_cost_per_unit = Number(v.outsource_cost_per_unit || 0);
      payload.outsourced_scope_detail = String(v.outsourced_scope_detail || '').trim() || null;
      payload.outsourced_supplier_category_ids = Array.isArray(v.outsourced_supplier_category_ids)
        ? v.outsourced_supplier_category_ids.filter((x: unknown) => typeof x === 'string' && String(x).trim())
        : [];
      payload.setup_hours = 0;
      payload.run_hours_per_unit = 0;
      payload.labor_rate = 0;
      payload.machine_rate = 0;
      payload.tooling_cost = 0;
    } else {
      payload.setup_hours = Number(v.setup_hours || 0);
      payload.run_hours_per_unit = Number(v.run_hours_per_unit || 0);
      payload.productivity_units_per_hour = v.productivity_units_per_hour;
      payload.crew_size = Number(v.crew_size || 1);
      payload.labor_grade_code = v.labor_grade_code || null;
      payload.machine_rate = Number(v.machine_rate || 0);
      payload.tooling_cost = Number(v.tooling_cost || 0);
      payload.outsource_cost_per_unit = 0;
      payload.outsourced_scope_detail = null;
      payload.outsourced_supplier_category_ids = null;
      payload.is_override = Boolean(v.override_reason);
      payload.override_reason = v.override_reason || null;
      if (v.operation_id && (v.labor_rate === 0 || v.labor_rate === '0') && !v.override_reason) {
        // Let backend resolve labor from operation master / rate card
      } else {
        payload.labor_rate = Number(v.labor_rate || 0);
      }
    }

    let res: unknown;
    if (editingOperation?.id) {
      res = await assemblyEstimateService.updateOperation(estimate.id, editingOperation.id, payload);
      message.success('Operation updated');
    } else {
      if (!selected) throw new Error('Select an assembly first');
      res = await assemblyEstimateService.addOperation(estimate.id, selected.id, payload);
      message.success('Operation added');
    }
    setOperationModalOpen(false);
    setEditingOperation(null);
    operationForm.resetFields();
    if (!applyDetailResponse(res)) await reload();
  };

  const applyMasterOperation = (
    operationIdOrOp: string | MasterOperation | undefined,
    formInstance: typeof operationForm
  ) => {
    if (!operationIdOrOp) {
      formInstance.setFieldsValue({ operation_id: undefined });
      return;
    }
    const selectedOp =
      typeof operationIdOrOp === 'string'
        ? masterOperations.find((op) => op.id === operationIdOrOp)
        : operationIdOrOp;
    if (!selectedOp) return;
    const desc = selectedOp.description
      ? `${selectedOp.operation_name} - ${selectedOp.description}`
      : selectedOp.operation_name;
    formInstance.setFieldsValue({
      operation_id: selectedOp.id,
      description: desc,
      setup_hours: Number(selectedOp.standard_setup_time_hours) || 0,
      run_hours_per_unit: Number(selectedOp.standard_run_time_per_unit_hours) || 0,
      machine_rate: Number(selectedOp.per_hour_cost) || 0,
      labor_rate: Number(selectedOp.per_hour_cost) || 0,
    });
  };

  const nodeActionItems: MenuProps['items'] = selected && !readOnly
    ? [
        {
          key: 'up',
          label: 'Move up',
          onClick: () => run(() => assemblyEstimateService.reorderNode(estimate.id, selected.id, 'up'), 'Moved up'),
        },
        {
          key: 'down',
          label: 'Move down',
          onClick: () => run(() => assemblyEstimateService.reorderNode(estimate.id, selected.id, 'down'), 'Moved down'),
        },
        {
          key: 'child',
          label: 'Add child',
          onClick: () =>
            run(
              () =>
                assemblyEstimateService.addNode(estimate.id, {
                  parent_id: selected.id,
                  node_name: 'Sub-assembly',
                  quantity_per_parent: 1,
                  uom: 'EA',
                  strategy: 'MAKE',
                }),
              'Sub-assembly added'
            ),
        },
        {
          key: 'copy',
          label: 'Copy',
          onClick: () => run(() => assemblyEstimateService.copyNode(estimate.id, selected.id), 'Copied'),
        },
        {
          key: 'move',
          label: 'Move resources',
          onClick: () => setMoveOpen(true),
        },
        { type: 'divider' },
        {
          key: 'delete',
          label: 'Delete',
          danger: true,
          onClick: confirmDeleteNode,
        },
      ]
    : [];

  const categoryRollupRows = selected
    ? [
        { key: 'material', category: 'Material', amount: selected.material_cost },
        { key: 'equipment', category: 'Equipment', amount: selected.equipment_cost },
        { key: 'manpower', category: 'Manpower', amount: selected.manpower_cost },
        { key: 'subcontract', category: 'Subcontract', amount: selected.subcontract_cost },
        { key: 'tooling', category: 'Tooling', amount: selected.tooling_cost },
      ].filter((row) => Number(row.amount || 0) !== 0)
    : [];

  const materialColumns = [
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Qty', dataIndex: 'net_qty_per_unit', width: 80 },
    { title: 'UOM', dataIndex: 'uom', width: 70 },
    { title: 'Rate', dataIndex: 'rate', width: 90, render: (v: number) => money(v) },
    {
      title: 'Amount',
      key: 'amount',
      width: 100,
      render: (_: unknown, row: Record<string, any>) =>
        money(Number(row.rate || 0) * Number(row.net_qty_per_unit || 0)),
    },
    {
      title: 'Source',
      key: 'source',
      width: 110,
      render: (_: unknown, row: Record<string, any>) => rateSourceTag(row.rate_source, Boolean(row.is_override)),
    },
    {
      title: 'Cust. supplied',
      dataIndex: 'customer_supplied',
      width: 100,
      render: (v: boolean) => (v ? 'Yes' : ''),
    },
    ...(showAdvancedMaterialCols
      ? [
          { title: 'Yield %', dataIndex: 'yield_percent', width: 80 },
          { title: 'Scrap %', dataIndex: 'scrap_percent', width: 80 },
          { title: 'Scrap credit', dataIndex: 'scrap_credit_rate', width: 100 },
          { title: 'Source ccy', dataIndex: 'source_currency', width: 90 },
          { title: 'FX', dataIndex: 'fx_rate', width: 70 },
          {
            title: 'Override reason',
            dataIndex: 'override_reason',
            render: (v: string, row: Record<string, any>) =>
              row.is_override ? (v || <Text type="danger">Missing</Text>) : '',
          },
          {
            title: 'Override by / at',
            key: 'override_audit',
            render: (_: unknown, row: Record<string, any>) =>
              row.override_by || row.override_at
                ? `${row.override_by || '—'} · ${row.override_at ? String(row.override_at) : '—'}`
                : '',
          },
        ]
      : []),
    ...(!readOnly
      ? [
          {
            title: '',
            key: 'actions',
            width: 120,
            fixed: 'right' as const,
            render: (_: unknown, row: Record<string, any>) => (
              <Space size={4}>
                <Button
                  size="small"
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditingMaterial(row);
                    editMaterialForm.setFieldsValue({
                      description: row.description,
                      net_qty_per_unit: Number(row.net_qty_per_unit),
                      yield_percent: Number(row.yield_percent ?? 100),
                      scrap_percent: Number(row.scrap_percent ?? 0),
                      scrap_credit_rate: Number(row.scrap_credit_rate ?? 0),
                      uom: row.uom,
                      source_currency: row.source_currency,
                      rate: Number(row.rate),
                      override_reason: row.override_reason,
                      customer_supplied: Boolean(row.customer_supplied),
                    });
                  }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  type="link"
                  danger
                  onClick={() => run(() => assemblyEstimateService.deleteMaterial(estimate.id, row.id), 'Removed')}
                >
                  Remove
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const operationColumns = [
    { title: 'Operation', dataIndex: 'description', ellipsis: true },
    { title: 'Setup', dataIndex: 'setup_hours', width: 70 },
    { title: 'Run', dataIndex: 'run_hours_per_unit', width: 70 },
    { title: 'Labor', dataIndex: 'labor_rate', width: 90, render: (v: number) => money(v) },
    { title: 'Machine', dataIndex: 'machine_rate', width: 90, render: (v: number) => money(v) },
    {
      title: 'Outsourced',
      dataIndex: 'is_outsourced',
      width: 90,
      render: (v: boolean) => (v ? 'Yes' : ''),
    },
    ...(showAdvancedOperationCols
      ? [
          { title: 'Productivity', dataIndex: 'productivity_units_per_hour', width: 100 },
          { title: 'Crew', dataIndex: 'crew_size', width: 70 },
          { title: 'Grade', dataIndex: 'labor_grade_code', width: 90 },
          { title: 'Tooling', dataIndex: 'tooling_cost', width: 90 },
          { title: 'Source ccy', dataIndex: 'source_currency', width: 90 },
          {
            title: 'Source',
            key: 'source',
            render: (_: unknown, row: Record<string, any>) => rateSourceTag(row.rate_source, Boolean(row.is_override)),
          },
          {
            title: 'Override reason',
            dataIndex: 'override_reason',
            render: (v: string, row: Record<string, any>) =>
              row.is_override ? (v || <Text type="danger">Missing</Text>) : '',
          },
          {
            title: 'Override by / at',
            key: 'override_audit',
            render: (_: unknown, row: Record<string, any>) =>
              row.override_by || row.override_at
                ? `${row.override_by || '—'} · ${row.override_at ? String(row.override_at) : '—'}`
                : '',
          },
        ]
      : []),
    ...(!readOnly
      ? [
          {
            title: '',
            key: 'actions',
            width: 120,
            fixed: 'right' as const,
            render: (_: unknown, row: Record<string, any>) => (
              <Space size={4}>
                <Button
                  size="small"
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => openEditOperationModal(row)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  type="link"
                  danger
                  onClick={() => run(() => assemblyEstimateService.deleteOperation(estimate.id, row.id), 'Removed')}
                >
                  Remove
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  const selectedNodePanel = selected ? (
    <Card
      styles={{ body: { paddingTop: 8 } }}
      title={
        <Space wrap size={8}>
          <Text strong>{selected.node_name}</Text>
          <Tag color={selectedIsLeaf ? 'cyan' : 'geekblue'}>{selectedIsLeaf ? 'Leaf' : 'Parent'}</Tag>
          <Tag>{selected.strategy}</Tag>
          {!selected.parent_id && !selected.is_phantom ? <Tag color="gold">Quote</Tag> : null}
        </Space>
      }
      extra={
        !readOnly ? (
          <Dropdown menu={{ items: nodeActionItems }} trigger={['click']}>
            <Button size="small" icon={<MoreOutlined />}>
              Actions
            </Button>
          </Dropdown>
        ) : null
      }
    >
      <Tabs
        size="small"
        activeKey={nodeWorkspaceTab}
        onChange={setNodeWorkspaceTab}
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Descriptions size="small" column={3}>
                  <Descriptions.Item label="Qty / parent">
                    {selected.quantity_per_parent} {selected.uom}
                  </Descriptions.Item>
                  <Descriptions.Item label="Extended qty">{selected.extended_qty}</Descriptions.Item>
                  <Descriptions.Item label="Direct">{money(selected.direct_cost)}</Descriptions.Item>
                  <Descriptions.Item label="Estimated">{money(selected.estimated_cost)}</Descriptions.Item>
                  <Descriptions.Item label="Selling">{money(selected.selling_price)}</Descriptions.Item>
                  <Descriptions.Item label="Quoted">{selected.quotation_id ? 'Yes' : 'No'}</Descriptions.Item>
                </Descriptions>

                {canCostSelected && materials.length === 0 && operations.length === 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    message="This assembly has no cost yet"
                    description="Add materials and process operations to build direct cost. Parents may also carry assembly-level materials and operations."
                    action={
                      <Space direction="vertical">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => {
                            setNodeWorkspaceTab('materials');
                            openAddMaterialModal();
                          }}
                        >
                          Add material
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setNodeWorkspaceTab('operations');
                            openAddOperationModal();
                          }}
                        >
                          Add Operation
                        </Button>
                      </Space>
                    }
                  />
                ) : null}

                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Category roll-up
                  </Text>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="key"
                    dataSource={
                      categoryRollupRows.length
                        ? categoryRollupRows
                        : [
                            { key: 'material', category: 'Material', amount: selected.material_cost },
                            { key: 'equipment', category: 'Equipment', amount: selected.equipment_cost },
                            { key: 'manpower', category: 'Manpower', amount: selected.manpower_cost },
                            { key: 'subcontract', category: 'Subcontract', amount: selected.subcontract_cost },
                            { key: 'tooling', category: 'Tooling', amount: selected.tooling_cost },
                          ]
                    }
                    columns={[
                      { title: 'Category', dataIndex: 'category' },
                      {
                        title: 'Amount',
                        dataIndex: 'amount',
                        align: 'right' as const,
                        render: (v: number) => money(v),
                      },
                    ]}
                  />
                </div>

                {!selectedIsLeaf && (
                  <Alert
                    type="info"
                    showIcon
                    message="Parent assembly"
                    description="Child costs roll up automatically. You can also add materials and operations here for assembly-level work — strategy becomes HYBRID when both exist."
                  />
                )}
                {selectedReadiness.length > 0 ? (
                  <Text type="warning" style={{ fontSize: 12 }}>
                    This assembly has {selectedReadiness.length} readiness issue
                    {selectedReadiness.length === 1 ? '' : 's'} (see page banner).
                  </Text>
                ) : null}

                {!readOnly && (
                  <Form
                    key={selected.id}
                    layout="vertical"
                    initialValues={{
                      strategy: selected.strategy,
                      quantity_per_parent: Number(selected.quantity_per_parent),
                      uom: selected.uom,
                      node_name: selected.node_name,
                      discount_amount: Number(selected.discount_amount || 0),
                    }}
                    onFinish={(v) =>
                      run(() => assemblyEstimateService.updateNode(estimate.id, selected.id, v), 'Assembly updated')
                    }
                  >
                    <Row gutter={12}>
                      <Col span={10}>
                        <Form.Item name="node_name" label="Name" rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item name="strategy" label="Strategy">
                          <Select
                            options={['MAKE', 'BUY', 'SUBCONTRACT', 'HYBRID'].map((v) => ({ value: v, label: v }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name="quantity_per_parent" label="Qty">
                          <InputNumber min={0.0001} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name="uom" label="UOM">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item name="discount_amount" label="Discount">
                          <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button htmlType="submit" type="primary" size="small" icon={<SaveOutlined />}>
                      Save assembly
                    </Button>
                  </Form>
                )}
              </Space>
            ),
          },
          {
            key: 'materials',
            label: `Materials (${materials.length})`,
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  {canCostSelected ? (
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddMaterialModal}>
                      Add material
                    </Button>
                  ) : (
                    <span />
                  )}
                  <Space size={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Advanced columns
                    </Text>
                    <Switch size="small" checked={showAdvancedMaterialCols} onChange={setShowAdvancedMaterialCols} />
                  </Space>
                </div>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: showAdvancedMaterialCols ? 1100 : 720 }}
                  dataSource={materials}
                  locale={{
                    emptyText: 'No materials on this assembly yet.',
                  }}
                  columns={materialColumns}
                />
              </Space>
            ),
          },
          {
            key: 'operations',
            label: `Operations (${operations.length})`,
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  {canCostSelected ? (
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddOperationModal}>
                      Add Operation
                    </Button>
                  ) : (
                    <span />
                  )}
                  <Space size={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Advanced columns
                    </Text>
                    <Switch size="small" checked={showAdvancedOperationCols} onChange={setShowAdvancedOperationCols} />
                  </Space>
                </div>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: showAdvancedOperationCols ? 1100 : 640 }}
                  dataSource={operations}
                  locale={{
                    emptyText: 'No operations on this assembly yet.',
                  }}
                  columns={operationColumns}
                />
              </Space>
            ),
          },
          {
            key: 'more',
            label: 'More',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card
                  size="small"
                  title="Estimate vs actual"
                  extra={
                    selected.actual_captured_at ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Last captured{' '}
                        {new Date(String(selected.actual_captured_at)).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </Text>
                    ) : null
                  }
                >
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Identity capture only"
                    description="Record actual cost by category for lineage and estimate-vs-actual review. This does not post stock, labor, or subcontract postings from the estimate."
                  />
                  <Form
                    form={actualsForm}
                    key={`actuals-${selected.id}-${selected.actual_captured_at || 'new'}`}
                    layout="vertical"
                    initialValues={{
                      actual_material_cost: selected.actual_material_cost ?? undefined,
                      actual_equipment_cost: selected.actual_equipment_cost ?? undefined,
                      actual_manpower_cost: selected.actual_manpower_cost ?? undefined,
                      actual_subcontract_cost: selected.actual_subcontract_cost ?? undefined,
                      actual_tooling_cost: selected.actual_tooling_cost ?? undefined,
                    }}
                    onFinish={(v) =>
                      run(() => assemblyEstimateService.setNodeActuals(estimate.id, selected.id, v), 'Actuals saved')
                    }
                  >
                    <Table<ActualCompareRow>
                      size="small"
                      pagination={false}
                      rowKey="key"
                      style={{ marginBottom: 12 }}
                      dataSource={[
                        {
                          key: 'material',
                          category: 'Material',
                          estimated: Number(selected.material_cost || 0),
                          field: 'actual_material_cost',
                          actual: selected.actual_material_cost,
                        },
                        {
                          key: 'equipment',
                          category: 'Equipment',
                          estimated: Number(selected.equipment_cost || 0),
                          field: 'actual_equipment_cost',
                          actual: selected.actual_equipment_cost,
                        },
                        {
                          key: 'manpower',
                          category: 'Manpower',
                          estimated: Number(selected.manpower_cost || 0),
                          field: 'actual_manpower_cost',
                          actual: selected.actual_manpower_cost,
                        },
                        {
                          key: 'subcontract',
                          category: 'Subcontract',
                          estimated: Number(selected.subcontract_cost || 0),
                          field: 'actual_subcontract_cost',
                          actual: selected.actual_subcontract_cost,
                        },
                        {
                          key: 'tooling',
                          category: 'Tooling',
                          estimated: Number(selected.tooling_cost || 0),
                          field: 'actual_tooling_cost',
                          actual: selected.actual_tooling_cost,
                        },
                      ]}
                      summary={() => {
                        const estimatedTotal =
                          Number(selected.material_cost || 0) +
                          Number(selected.equipment_cost || 0) +
                          Number(selected.manpower_cost || 0) +
                          Number(selected.subcontract_cost || 0) +
                          Number(selected.tooling_cost || 0);
                        const actualTotal =
                          Number(selected.actual_material_cost || 0) +
                          Number(selected.actual_equipment_cost || 0) +
                          Number(selected.actual_manpower_cost || 0) +
                          Number(selected.actual_subcontract_cost || 0) +
                          Number(selected.actual_tooling_cost || 0);
                        const hasActual = [
                          selected.actual_material_cost,
                          selected.actual_equipment_cost,
                          selected.actual_manpower_cost,
                          selected.actual_subcontract_cost,
                          selected.actual_tooling_cost,
                        ].some((v) => v != null);
                        const variance = hasActual ? actualTotal - estimatedTotal : null;
                        return (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>
                              <Text strong>Total</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              <Text strong>{money(estimatedTotal)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="right">
                              <Text strong>{hasActual ? money(actualTotal) : '—'}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                              <Text strong type={variance == null ? undefined : variance > 0 ? 'danger' : variance < 0 ? 'success' : undefined}>
                                {variance == null ? '—' : money(variance)}
                              </Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                      columns={[
                        { title: 'Category', dataIndex: 'category', width: 140 },
                        {
                          title: 'Estimated',
                          dataIndex: 'estimated',
                          align: 'right',
                          width: 120,
                          render: (v: number) => money(v),
                        },
                        {
                          title: 'Actual',
                          width: 160,
                          render: (_: unknown, row) =>
                            readOnly ? (
                              row.actual == null ? '—' : money(row.actual)
                            ) : (
                              <Form.Item name={row.field} style={{ marginBottom: 0 }}>
                                <InputNumber style={{ width: '100%' }} min={0} placeholder="—" />
                              </Form.Item>
                            ),
                        },
                        {
                          title: 'Variance',
                          width: 120,
                          align: 'right',
                          render: (_: unknown, row) => {
                            if (row.actual == null) return '—';
                            const delta = Number(row.actual) - Number(row.estimated || 0);
                            return (
                              <Text type={delta > 0 ? 'danger' : delta < 0 ? 'success' : undefined}>{money(delta)}</Text>
                            );
                          },
                        },
                      ]}
                    />
                    {!readOnly ? (
                      <Button type="primary" htmlType="submit" size="small" icon={<SaveOutlined />}>
                        Save actuals
                      </Button>
                    ) : null}
                  </Form>
                </Card>
              </Space>
            ),
          },
        ]}
      />
    </Card>
  ) : null;

  const assembliesTab = !hasNodes ? (
    assembliesEmpty
  ) : (
    <Row gutter={16}>
      <Col xs={24} lg={8} xl={7}>
        <Card
          title="Assemblies"
          styles={{ body: { paddingTop: 8, paddingBottom: 12 } }}
          extra={
            !readOnly && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  run(
                    () =>
                      assemblyEstimateService.addNode(estimate.id, {
                        node_name: 'New assembly',
                        quantity_per_parent: 1,
                        uom: 'EA',
                        strategy: 'MAKE',
                      }),
                    'Assembly added'
                  )
                }
              >
                Add top level
              </Button>
            )
          }
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
            Select an assembly to cost materials and operations on the right.
          </Text>
          <div className="abe-assembly-tree">
            <Tree
              blockNode
              showLine={{ showLeafIcon: false }}
              height={520}
              virtual
              treeData={treeData}
              selectedKeys={selected ? [selected.id] : []}
              onSelect={(keys) => {
                if (!keys.length) return;
                setSelectedId(String(keys[0]));
                setNodeWorkspaceTab('overview');
              }}
              defaultExpandAll
            />
          </div>
        </Card>
      </Col>
      <Col xs={24} lg={16} xl={17}>{selectedNodePanel}</Col>
    </Row>
  );

  const openIndirectModal = (row?: Record<string, any>) => {
    setEditingIndirect(row || null);
    setIndirectModalOpen(true);
    indirectForm.setFieldsValue(
      row
        ? {
            category: row.category,
            scope: row.scope || 'ESTIMATE',
            node_id: row.node_id || undefined,
            method: row.method || 'FIXED',
            fixed_amount: Number(row.fixed_amount ?? 0),
            percent_value: Number(row.percent_value ?? 0),
            quantity: Number(row.quantity ?? 0),
            rate: Number(row.rate ?? 0),
            duration_quantity: Number(row.duration_quantity ?? 0) || undefined,
            remarks: row.remarks || undefined,
          }
        : {
            category: undefined,
            scope: 'ESTIMATE',
            node_id: undefined,
            method: 'FIXED',
            fixed_amount: 0,
            percent_value: 0,
            quantity: 0,
            rate: 0,
            duration_quantity: undefined,
            remarks: undefined,
          }
    );
  };

  const submitIndirect = async () => {
    const v = await indirectForm.validateFields();
    if (v.scope === 'TOP_LEVEL' && !v.node_id) {
      message.error('Select a top-level assembly for this indirect');
      throw new Error('node_id required');
    }
    const res = await assemblyEstimateService.saveIndirect(
      estimate.id,
      {
        ...v,
        node_id: v.scope === 'TOP_LEVEL' ? v.node_id : null,
        duration_quantity: v.duration_quantity ?? 0,
      },
      editingIndirect?.id
    );
    message.success(editingIndirect ? 'Indirect updated' : 'Indirect added');
    setIndirectModalOpen(false);
    setEditingIndirect(null);
    indirectForm.resetFields();
    if (!applyDetailResponse(res)) await reload();
  };

  const openEscalationModal = (row?: Record<string, any>) => {
    setEditingEscalation(row || null);
    setEscalationModalOpen(true);
    escalationForm.setFieldsValue(
      row
        ? {
            cost_category: row.cost_category || 'ALL',
            percent_value: Number(row.percent_value ?? 0),
            period_label: row.period_label || undefined,
            remarks: row.remarks || undefined,
          }
        : { cost_category: 'ALL', percent_value: 0, period_label: undefined, remarks: undefined }
    );
  };

  const submitEscalation = async () => {
    const v = await escalationForm.validateFields();
    const res = await assemblyEstimateService.saveEscalation(estimate.id, v, editingEscalation?.id);
    message.success(editingEscalation ? 'Escalation updated' : 'Escalation added');
    setEscalationModalOpen(false);
    setEditingEscalation(null);
    escalationForm.resetFields();
    if (!applyDetailResponse(res)) await reload();
  };

  const commercialTab = (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="Pricing, contingency, and corporate overhead">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Corporate overhead is company policy — separate from project/site indirect lines below."
        />
        {!readOnly && (
          <Form
            key={`commercial-${estimate.row_version}`}
            layout="vertical"
            initialValues={{
              contingency_percent: Number(estimate.contingency_percent),
              pricing_method: estimate.pricing_method,
              pricing_percent: Number(estimate.pricing_percent),
              corporate_overhead_method: estimate.corporate_overhead_method,
              corporate_overhead_value: Number(estimate.corporate_overhead_value),
              corporate_overhead_placement: estimate.corporate_overhead_placement,
              duration_quantity: Number(estimate.duration_quantity),
              min_margin_percent: estimate.min_margin_percent,
            }}
            onFinish={(v) =>
              run(
                () => assemblyEstimateService.update(estimate.id, { ...v, row_version: estimate.row_version }),
                'Commercial terms saved'
              )
            }
          >
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="pricing_method" label="Price method">
                  <Select options={[{ value: 'MARKUP', label: 'Markup' }, { value: 'MARGIN', label: 'Margin' }]} />
                </Form.Item>
              </Col>
              <Col span={8}><Form.Item name="pricing_percent" label="Percent"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={8}><Form.Item name="contingency_percent" label="Contingency %"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={8}>
                <Form.Item name="corporate_overhead_method" label="Corporate overhead">
                  <Select options={[{ value: 'PERCENT', label: 'Percent' }, { value: 'FIXED', label: 'Fixed' }]} />
                </Form.Item>
              </Col>
              <Col span={8}><Form.Item name="corporate_overhead_value" label="Overhead value"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={8}>
                <Form.Item name="corporate_overhead_placement" label="Overhead placement">
                  <Select
                    options={[
                      { value: 'INCLUDE_IN_ESTIMATED_COST', label: 'In estimated cost' },
                      { value: 'SELLING_BUILDUP', label: 'Selling build-up only' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="duration_quantity"
                  label="Estimate duration"
                  tooltip="Default duration used by duration-based project indirects when a line leaves duration blank."
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
              <Col span={8}><Form.Item name="min_margin_percent" label="Min margin %"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
            <Button htmlType="submit" size="small" icon={<SaveOutlined />}>Save commercial terms</Button>
          </Form>
        )}
        {readOnly ? (
          <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
            <Descriptions.Item label="Price">{estimate.pricing_method} {Number(estimate.pricing_percent || 0)}%</Descriptions.Item>
            <Descriptions.Item label="Contingency">{Number(estimate.contingency_percent || 0)}%</Descriptions.Item>
            <Descriptions.Item label="Corp. overhead">
              {estimate.corporate_overhead_method} {Number(estimate.corporate_overhead_value || 0)}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">{Number(estimate.duration_quantity || 0)}</Descriptions.Item>
          </Descriptions>
        ) : null}
      </Card>

      <Card
        title="Project / site indirects"
        extra={
          !readOnly ? (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openIndirectModal()}>
              Add indirect
            </Button>
          ) : null
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="These are project indirect costs (site, utilities, facilities). They are not the same as corporate overhead above."
        />
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={detail.indirects}
          locale={{ emptyText: 'No project indirects yet' }}
          columns={[
            { title: 'Category', dataIndex: 'category', ellipsis: true },
            {
              title: 'Scope',
              width: 160,
              render: (_: unknown, row: Record<string, any>) => {
                if (row.scope === 'TOP_LEVEL') {
                  const node = detail.nodes.find((n) => n.id === row.node_id);
                  return <span>Top · {node?.node_name || '—'}</span>;
                }
                return 'Estimate';
              },
            },
            {
              title: 'Method',
              dataIndex: 'method',
              width: 140,
              render: (v: string) => INDIRECT_METHOD_OPTIONS.find((o) => o.value === v)?.label || v,
            },
            {
              title: 'Basis',
              width: 160,
              render: (_: unknown, row: Record<string, any>) => {
                if (row.method === 'PERCENT_DIRECT') return `${Number(row.percent_value || 0)}%`;
                if (row.method === 'QTY_RATE') return `${Number(row.quantity || 0)} × ${Number(row.rate || 0)}`;
                if (row.method === 'DURATION_RATE') {
                  const dur = Number(row.duration_quantity || 0) || Number(estimate.duration_quantity || 0);
                  return `${dur} × ${Number(row.rate || 0)}`;
                }
                return money(row.fixed_amount);
              },
            },
            {
              title: 'Amount',
              width: 110,
              align: 'right' as const,
              render: (_: unknown, row: Record<string, any>) =>
                money(detail.indirectAmounts?.[row.id] ?? row.fixed_amount),
            },
            {
              title: 'Remarks',
              dataIndex: 'remarks',
              ellipsis: true,
              render: (v: string | null) => v || '—',
            },
            ...(!readOnly
              ? [{
                  title: '',
                  width: 140,
                  render: (_: unknown, row: Record<string, any>) => (
                    <Space size={4}>
                      <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openIndirectModal(row)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          run(() => assemblyEstimateService.deleteIndirect(estimate.id, row.id), 'Indirect removed')
                        }
                      >
                        Delete
                      </Button>
                    </Space>
                  ),
                }]
              : []),
          ]}
        />
      </Card>

      <Card
        title="Escalations"
        extra={
          !readOnly ? (
            <Button size="small" icon={<PlusOutlined />} onClick={() => openEscalationModal()}>
              Add escalation
            </Button>
          ) : null
        }
      >
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={detail.escalations || []}
          locale={{ emptyText: 'No escalations' }}
          columns={[
            { title: 'Category', dataIndex: 'cost_category' },
            {
              title: '%',
              dataIndex: 'percent_value',
              width: 90,
              render: (v: number) => `${Number(v || 0)}%`,
            },
            { title: 'Period', dataIndex: 'period_label', render: (v: string | null) => v || '—' },
            { title: 'Remarks', dataIndex: 'remarks', ellipsis: true, render: (v: string | null) => v || '—' },
            ...(!readOnly
              ? [{
                  title: '',
                  width: 140,
                  render: (_: unknown, row: Record<string, any>) => (
                    <Space size={4}>
                      <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openEscalationModal(row)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() =>
                          run(() => assemblyEstimateService.deleteEscalation(estimate.id, row.id), 'Escalation removed')
                        }
                      >
                        Delete
                      </Button>
                    </Space>
                  ),
                }]
              : []),
          ]}
        />
      </Card>

      <Card title="Statements">
        {detail.statements.map((s) => (
          <div key={s.id} style={{ marginBottom: 8 }}>
            <Tag>{s.kind}</Tag> {s.customer_facing ? <Tag color="green">Customer</Tag> : <Tag>Internal</Tag>} {s.body}
            {!readOnly && (
              <Button type="link" danger onClick={() => run(() => assemblyEstimateService.deleteStatement(estimate.id, s.id), 'Removed')}>
                Remove
              </Button>
            )}
          </div>
        ))}
        {!readOnly && (
          <Form layout="inline" onFinish={(v) => run(() => assemblyEstimateService.saveStatement(estimate.id, v), 'Statement added')}>
            <Form.Item name="kind" initialValue="ASSUMPTION">
              <Select
                style={{ width: 160 }}
                options={['ASSUMPTION', 'INCLUSION', 'EXCLUSION', 'QUALIFICATION', 'CLIENT_RESPONSIBILITY'].map((v) => ({
                  value: v,
                  label: v,
                }))}
              />
            </Form.Item>
            <Form.Item name="body" rules={[{ required: true }]}><Input placeholder="Statement" /></Form.Item>
            <Form.Item name="customer_facing" initialValue={false}>
              <Select style={{ width: 120 }} options={[{ value: false, label: 'Internal' }, { value: true, label: 'Customer' }]} />
            </Form.Item>
            <Button htmlType="submit" size="small">Add</Button>
          </Form>
        )}
      </Card>

      <Card title="Customer summary">
        {estimate.customer_summary_text ? (
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{estimate.customer_summary_text}</Paragraph>
        ) : (
          <Text type="secondary">No customer summary yet. Use Generate customer summary.</Text>
        )}
        {estimate.customer_summary_document_id ? (
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Attachment document: {estimate.customer_summary_document_id}</Text>
          </div>
        ) : null}
        <Space style={{ marginTop: 8 }} wrap>
          <Button
            size="small"
            onClick={() =>
              run(async () => {
                const res = await assemblyEstimateService.customerSummary(estimate.id);
                const text =
                  (res as { data?: { text?: string } })?.data?.text ||
                  (res as { text?: string })?.text ||
                  estimate.customer_summary_text ||
                  '';
                setCustomerSummaryText(String(text || ''));
                setCustomerSummaryOpen(true);
                await reload();
              }, 'Customer summary generated')
            }
          >
            Generate customer summary
          </Button>
          <Upload
            showUploadList={false}
            beforeUpload={async (file) => {
              await run(async () => {
                const doc = await documentService.uploadDocument(file, {
                  entity_type: 'assembly_estimate',
                  entity_id: estimate.id,
                  document_name: file.name,
                });
                await assemblyEstimateService.saveCustomerSummary(estimate.id, { document_id: doc.id });
              }, 'Customer summary attachment saved');
              return false;
            }}
          >
            <Button size="small">Upload summary attachment</Button>
          </Upload>
        </Space>
      </Card>
    </Space>
  );

  const offersTab = (
    <Card
      title="Budgetary offers"
      extra={
        !readOnly ? (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openOfferModal()}>
            Add offer
          </Button>
        ) : null
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Capture supplier budgetary quotes against a material or operation line, then apply one as the working cost."
      />
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={detail.offers}
        locale={{ emptyText: 'No budgetary offers yet' }}
        columns={[
          {
            title: 'Type',
            dataIndex: 'target_type',
            width: 100,
            render: (v: string) => <Tag>{v || 'MATERIAL'}</Tag>,
          },
          {
            title: 'Line',
            ellipsis: true,
            render: (_: unknown, row: Record<string, any>) => resolveOfferTargetLabel(row),
          },
          {
            title: 'Supplier',
            ellipsis: true,
            render: (_: unknown, row: Record<string, any>) => row.supplier_name || row.supplier_id || '—',
          },
          {
            title: 'Amount',
            width: 130,
            align: 'right' as const,
            render: (_: unknown, row: Record<string, any>) => (
              <span>
                {money(row.amount)} {row.currency || estimate.currency}
              </span>
            ),
          },
          {
            title: 'Note',
            dataIndex: 'note',
            ellipsis: true,
            render: (v: string | null) => v || '—',
          },
          {
            title: 'Attachment',
            width: 100,
            render: (_: unknown, row: Record<string, any>) =>
              row.attachment_url ? (
                <a href={row.attachment_url} target="_blank" rel="noreferrer">
                  Open
                </a>
              ) : row.attachment_document_id ? (
                <Text type="secondary">Doc</Text>
              ) : (
                '—'
              ),
          },
          {
            title: 'Status',
            width: 100,
            render: (_: unknown, row: Record<string, any>) =>
              row.is_selected ? <Tag color="green">Selected</Tag> : <Tag>Open</Tag>,
          },
          ...(!readOnly
            ? [{
                title: '',
                width: 220,
                render: (_: unknown, row: Record<string, any>) => (
                  <Space size={0} wrap>
                    {!row.is_selected ? (
                      <Button
                        size="small"
                        type="link"
                        onClick={() =>
                          run(() => assemblyEstimateService.selectOffer(estimate.id, row.id), 'Offer applied to line')
                        }
                      >
                        Use cost
                      </Button>
                    ) : null}
                    {!row.is_selected ? (
                      <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openOfferModal(row)}>
                        Edit
                      </Button>
                    ) : null}
                    {!row.is_selected ? (
                      <Button
                        size="small"
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          run(() => assemblyEstimateService.deleteOffer(estimate.id, row.id), 'Offer removed')
                        }
                      >
                        Delete
                      </Button>
                    ) : null}
                  </Space>
                ),
              }]
            : []),
        ]}
      />
    </Card>
  );

  const publishedTemplates = (templatesQuery.data || []).filter((t) => t.status === 'PUBLISHED');
  const draftTemplates = (templatesQuery.data || []).filter((t) => t.status === 'DRAFT');
  const retiredTemplates = (templatesQuery.data || []).filter((t) => t.status === 'RETIRED');
  const templateRows = [...publishedTemplates, ...draftTemplates, ...retiredTemplates];

  const toolsTab = (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title="Excel import / export">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Prepare the tree in Excel, then import into an empty Draft estimate."
          description="Download the template (Assemblies + Resources + Instructions). Export copies the current tree in the same format. Import requires no existing nodes."
        />
        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              assemblyEstimateService
                .downloadImportTemplate()
                .then(() => message.success('Template downloaded'))
                .catch((error: any) => message.error(error?.response?.data?.message || error?.message || 'Download failed'))
            }
          >
            Download import template
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              assemblyEstimateService
                .downloadExport(estimate.id, estimate.estimate_number)
                .then(() => message.success('Exported'))
                .catch((error: any) => message.error(error?.response?.data?.message || error?.message || 'Export failed'))
            }
          >
            Export Excel
          </Button>
          {!readOnly ? (
            <Upload
              showUploadList={false}
              accept=".xlsx,.xls"
              beforeUpload={async (file) => {
                try {
                  const preview = await assemblyEstimateService.previewImport(estimate.id, file);
                  const errors = preview?.data?.errors || [];
                  const warnings = preview?.data?.warnings || [];
                  if (errors.length) {
                    message.error(errors.slice(0, 3).join(' · '));
                    return false;
                  }
                  Modal.confirm({
                    title: `Import ${preview?.data?.rowCount || 0} rows?`,
                    content: (
                      <div>
                        <p>Clear the assembly tree first if nodes already exist.</p>
                        {warnings.length ? (
                          <>
                            <p>Warnings:</p>
                            <ul>{warnings.slice(0, 5).map((w: string) => <li key={w}>{w}</li>)}</ul>
                          </>
                        ) : null}
                      </div>
                    ),
                    onOk: () => run(() => assemblyEstimateService.confirmImport(estimate.id, file), 'Imported'),
                  });
                } catch (error: any) {
                  message.error(error?.response?.data?.message || error?.message || 'Preview failed');
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>Import Excel</Button>
            </Upload>
          ) : null}
        </Space>
      </Card>

      <Card
        title="Assembly templates"
        extra={
          !readOnly && selected ? (
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                run(
                  () =>
                    assemblyEstimateService
                      .saveNodeTemplate(estimate.id, selected.id, selected.node_name)
                      .then(() => templatesQuery.refetch()),
                  'Saved as draft template'
                )
              }
            >
              Save selected node
            </Button>
          ) : null
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Templates snapshot a structure for reuse. Publish before applying to an estimate."
        />
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            placeholder="Apply published template"
            style={{ width: 280 }}
            allowClear
            options={publishedTemplates.map((t) => ({ value: t.id, label: t.template_name }))}
            onChange={(templateId) => {
              if (!templateId) return;
              run(() => assemblyEstimateService.applyTemplate(estimate.id, templateId), 'Template applied');
            }}
            disabled={readOnly || publishedTemplates.length === 0}
          />
        </Space>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={templateRows}
          locale={{ emptyText: 'No templates yet — save a selected assembly as a draft' }}
          columns={[
            { title: 'Name', dataIndex: 'template_name', ellipsis: true },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 110,
              render: (v: string) => (
                <Tag color={v === 'PUBLISHED' ? 'green' : v === 'DRAFT' ? 'blue' : 'default'}>{v}</Tag>
              ),
            },
            ...(!readOnly
              ? [{
                  title: '',
                  width: 160,
                  render: (_: unknown, row: Record<string, any>) => (
                    <Space size={0}>
                      {row.status === 'DRAFT' ? (
                        <Button
                          size="small"
                          type="link"
                          onClick={() =>
                            run(
                              () =>
                                assemblyEstimateService
                                  .setTemplateStatus(row.id, 'PUBLISHED')
                                  .then(() => templatesQuery.refetch()),
                              'Published'
                            )
                          }
                        >
                          Publish
                        </Button>
                      ) : null}
                      {row.status === 'PUBLISHED' ? (
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() =>
                            run(
                              () =>
                                assemblyEstimateService
                                  .setTemplateStatus(row.id, 'RETIRED')
                                  .then(() => templatesQuery.refetch()),
                              'Retired'
                            )
                          }
                        >
                          Retire
                        </Button>
                      ) : null}
                    </Space>
                  ),
                }]
              : []),
          ]}
        />
      </Card>

      <Card
        title="Labor rate cards"
        extra={
          !readOnly ? (
            <Button size="small" icon={<PlusOutlined />} onClick={() => openRateCardModal()}>
              Add rate card
            </Button>
          ) : null
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Company labor grades used when costing operations. These are shared across assembly estimates."
        />
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={rateCardsQuery.data || []}
          locale={{ emptyText: 'No labor rate cards yet' }}
          columns={[
            { title: 'Grade', dataIndex: 'grade_code', width: 120 },
            { title: 'Name', dataIndex: 'grade_name', ellipsis: true },
            {
              title: 'Hourly rate',
              dataIndex: 'hourly_rate',
              width: 120,
              align: 'right' as const,
              render: (v: number, row: RateCard) => (
                <span>
                  {money(v)} {row.currency || estimate.currency}
                </span>
              ),
            },
            ...(!readOnly
              ? [{
                  title: '',
                  width: 80,
                  render: (_: unknown, row: RateCard) => (
                    <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openRateCardModal(row)}>
                      Edit
                    </Button>
                  ),
                }]
              : []),
          ]}
        />
      </Card>

      <Card title="Revision compare">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Compare cost stack and assembly lines against another revision of this estimate."
        />
        <Space wrap>
          <Select
            placeholder="Compare with revision"
            style={{ width: 280 }}
            value={compareRight || undefined}
            onChange={(value) => {
              setCompareRight(value);
              setCompareResult(null);
            }}
            options={(detail.revisions || [])
              .filter((rev) => rev.id !== estimate.id)
              .map((rev) => ({
                value: rev.id,
                label: `Rev ${rev.revision_number} · ${rev.status}`,
              }))}
          />
          <Button
            type="primary"
            disabled={!compareRight}
            onClick={() =>
              run(async () => {
                const res = await assemblyEstimateService.compare(estimate.id, compareRight);
                setCompareResult(res.data as CompareResult);
              }, 'Compared')
            }
          >
            Compare
          </Button>
        </Space>
        {compareResult && (
          <div style={{ marginTop: 12 }}>
            <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Direct Δ">{money(compareResult.stackDelta?.direct)}</Descriptions.Item>
              <Descriptions.Item label="Indirect Δ">{money(compareResult.stackDelta?.indirect)}</Descriptions.Item>
              <Descriptions.Item label="Contingency Δ">{money(compareResult.stackDelta?.contingency)}</Descriptions.Item>
              <Descriptions.Item label="Overhead Δ">{money(compareResult.stackDelta?.overhead)}</Descriptions.Item>
              <Descriptions.Item label="Estimated Δ">{money(compareResult.stackDelta?.estimated)}</Descriptions.Item>
              <Descriptions.Item label="Selling Δ">{money(compareResult.stackDelta?.selling)}</Descriptions.Item>
            </Descriptions>
            <Table
              size="small"
              rowKey="name"
              pagination={false}
              dataSource={compareResult.lines || []}
              columns={[
                { title: 'Assembly', dataIndex: 'name' },
                { title: 'Direct Δ', dataIndex: 'directDelta', render: (v: number) => money(v) },
                { title: 'Selling Δ', dataIndex: 'sellingDelta', render: (v: number) => money(v) },
                { title: 'Material Δ', render: (_: unknown, row: any) => money(row.bucketDeltas?.material) },
                { title: 'Equipment Δ', render: (_: unknown, row: any) => money(row.bucketDeltas?.equipment) },
                { title: 'Manpower Δ', render: (_: unknown, row: any) => money(row.bucketDeltas?.manpower) },
                { title: 'Subcontract Δ', render: (_: unknown, row: any) => money(row.bucketDeltas?.subcontract) },
                { title: 'Tooling Δ', render: (_: unknown, row: any) => money(row.bucketDeltas?.tooling) },
              ]}
            />
          </div>
        )}
      </Card>

      <Card title="Capabilities & boundaries">
        <Collapse
          size="small"
          items={boundaryEntries.map(([key, text]) => ({
            key,
            label: key,
            children: <Text type="secondary">{text}</Text>,
          }))}
        />
      </Card>
    </Space>
  );

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/growth/estimations/assembly')}
                style={{ marginBottom: 16 }}
              >
                Back to List
              </Button>
              <Title level={2} style={{ margin: 0 }}>
                {estimate.estimate_number} — {estimate.estimate_name}
              </Title>
              <Space size={8} style={{ marginTop: 4 }} wrap>
                <Tag color={statusColor}>{estimate.status}</Tag>
                <Text type="secondary">{subtitleParts.join(' · ')}</Text>
              </Space>
            </div>
            {headerActions}
          </div>
          {readOnly && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="This revision is frozen. Copy or revise it to change the cost."
            />
          )}
          {!readOnly && (
            <Collapse
              style={{ marginTop: 12 }}
              items={[
                {
                  key: 'details',
                  label: 'Estimate details',
                  children: (
                    <Form
                      form={headerForm}
                      layout="vertical"
                      key={`${estimate.id}-${estimate.row_version}`}
                      initialValues={{
                        estimate_name: estimate.estimate_name,
                        notes: estimate.notes,
                      }}
                      onFinish={(v) =>
                        run(
                          () =>
                            assemblyEstimateService.update(estimate.id, {
                              estimate_name: v.estimate_name,
                              notes: v.notes,
                              row_version: estimate.row_version,
                            }),
                          'Estimate details saved'
                        )
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="estimate_name" label="Estimate name" rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="notes" label="Notes">
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Button type="primary" htmlType="submit" icon={<SaveOutlined />} size="small">
                        Save details
                      </Button>
                    </Form>
                  ),
                },
              ]}
            />
          )}
        </Card>

        {costSummaryCard}
        {readinessBanner}

        {estimate.approval_instance_id ? (
          <Card size="small" title="Approval workflow">
            <ApprovalPanel
              entityType={ENTITY_TYPE_ASSEMBLY_ESTIMATE}
              entityId={estimate.id}
              onApproveViaDocument={async (remarks) => {
                await assemblyEstimateService.approve(estimate.id, remarks);
                message.success('Approval submitted');
                await reload();
              }}
              onRejectViaDocument={async (payload) => {
                await assemblyEstimateService.reject(estimate.id, payload);
                message.success('Returned to draft');
                await reload();
              }}
              extraInvalidateQueryKeys={[['assembly-estimate', estimate.id]]}
            />
          </Card>
        ) : null}

        <Card styles={{ body: { paddingTop: 8 } }}>
          <Tabs
            activeKey={mainTab}
            onChange={setMainTab}
            items={[
              { key: 'assemblies', label: 'Assemblies', children: assembliesTab },
              { key: 'commercial', label: 'Commercial', children: commercialTab },
              { key: 'offers', label: 'Offers', children: offersTab },
              { key: 'tools', label: 'Tools', children: toolsTab },
            ]}
          />
        </Card>
      </Space>

      {selected && (
        <Modal title="Move resources onto a leaf" open={moveOpen} onCancel={() => setMoveOpen(false)} footer={null}>
          <Select
            style={{ width: '100%' }}
            placeholder="Destination assembly"
            options={detail.nodes.filter((n) => n.id !== selected.id).map((n) => ({ value: n.id, label: n.node_name }))}
            onChange={(to) => {
              setMoveOpen(false);
              run(() => assemblyEstimateService.moveResources(estimate.id, selected.id, to), 'Resources moved');
            }}
          />
        </Modal>
      )}

      <Modal
        title="Add material"
        open={addMaterialOpen}
        onCancel={() => setAddMaterialOpen(false)}
        onOk={() =>
          submitAddMaterial().catch((error: any) => {
            message.error(error?.response?.data?.message || error?.message || 'Could not add material');
            return Promise.reject(error);
          })
        }
        okText="Add material"
        destroyOnClose
        width={720}
        afterOpenChange={(open) => {
          if (!open) return;
          materialForm.setFieldsValue({
            yield_percent: 100,
            scrap_percent: 0,
            scrap_credit_rate: 0,
            source_currency: estimate.currency || 'AED',
            net_qty_per_unit: 1,
            uom: 'EA',
            rate: 0,
            customer_supplied: false,
            description: undefined,
            item_id: undefined,
            override_reason: undefined,
          });
        }}
      >
        <Form form={materialForm} layout="vertical" preserve={false}>
          <Form.Item name="item_id" label="Inventory item">
            <ItemSelect
              allowClear
              placeholder="Inventory item"
              style={{ width: '100%' }}
              onChange={(val, option) => {
                const item = !Array.isArray(option)
                  ? (option as { item?: ItemLike & { average_cost?: number | null } } | undefined)?.item
                  : undefined;
                if (!val || !item) {
                  materialForm.setFieldValue('item_id', val ?? undefined);
                  return;
                }
                const rate = itemRate(item);
                const name = String(item.item_name || item.item_description || '').trim();
                const profile = formatItemProfileMeta(item, { includeLength: true });
                const description = [name, profile].filter(Boolean).join(' — ');
                materialForm.setFieldsValue({
                  item_id: item.id,
                  description: description || name,
                  uom: item.base_uom || item.purchase_uom || item.sales_uom || materialForm.getFieldValue('uom'),
                  ...(rate != null ? { rate } : {}),
                });
              }}
            />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Description is required' }]}>
            <Input placeholder="Material" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="net_qty_per_unit" label="Qty" initialValue={1}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="uom" label="UOM" initialValue="EA">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="rate" label="Rate" initialValue={0}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="yield_percent" label="Yield %" initialValue={100}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="scrap_percent" label="Scrap %" initialValue={0}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="scrap_credit_rate" label="Scrap credit rate" initialValue={0}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="source_currency" label="Currency" initialValue={estimate.currency || 'AED'}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="customer_supplied" label="Supply" initialValue={false}>
                <Select options={[{ value: false, label: 'Costed' }, { value: true, label: 'Customer supplied' }]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="override_reason" label="Override reason">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={editingOperation ? 'Edit Operation' : 'Add Operation'}
        open={operationModalOpen}
        onCancel={() => {
          setOperationModalOpen(false);
          setEditingOperation(null);
        }}
        onOk={() =>
          submitOperationModal().catch((error: any) => {
            message.error(error?.response?.data?.message || error?.message || 'Could not save operation');
            return Promise.reject(error);
          })
        }
        okText={editingOperation ? 'Save' : 'OK'}
        destroyOnClose
        width={800}
      >
        <Form form={operationForm} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col span={18}>
              <Form.Item
                name="operation_id"
                label="Operation"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('is_outsourced')) return Promise.resolve();
                      if (!value) return Promise.reject(new Error('Required'));
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Select
                  placeholder="Select operation (optional if outsourced)"
                  allowClear
                  loading={operationsMasterQuery.isLoading}
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onSearch={setOperationSelectSearch}
                  onChange={(value) => applyMasterOperation(value, operationForm)}
                  notFoundContent={
                    operationsMasterQuery.isLoading
                      ? 'Loading operations...'
                      : 'No matching operation — create one below'
                  }
                  popupRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        block
                        style={{ textAlign: 'left' }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setQuickCreateOperationOpen(true)}
                      >
                        Create new operation
                      </Button>
                    </>
                  )}
                  options={masterOperations.map((op) => ({
                    value: op.id,
                    label: `${op.operation_code} - ${op.operation_name}`,
                  }))}
                />
              </Form.Item>
              <Form.Item name="description" label="Operation Description" hidden>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="sequence_number" label="Sequence" initialValue={1}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="is_outsourced" label="Is this operation outsourced?" valuePropName="checked" initialValue={false}>
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          {operationOutsourcedWatch ? (
            <>
              <Form.Item
                name="outsource_cost_per_unit"
                label={`Outsource Cost (${estimate.currency || 'AED'})`}
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="Outsource cost per unit" />
              </Form.Item>
              <Form.Item
                name="outsourced_supplier_category_ids"
                label="Supplier categories (for procurement)"
                help="Helps purchasing shortlist suppliers for this outsourced step."
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select one or more supplier categories"
                  options={supplierCategorySelectOptions}
                  loading={loadingSupplierCategories}
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item
                name="outsourced_scope_detail"
                label="Operation outsourced scope detail"
                rules={[{ required: true, message: 'Describe the outsourced scope of work' }]}
                help="Vendor-facing scope for procurement."
              >
                <Input.TextArea rows={3} placeholder="Describe what the vendor must perform for this operation" />
              </Form.Item>
            </>
          ) : (
            <>
              <Divider>Time & Rates</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="setup_hours" label="Setup Time (hours)" initialValue={0}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="run_hours_per_unit" label="Run time (hours)" initialValue={0}>
                    <InputNumber min={0} precision={4} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="labor_grade_code" label="Labor grade">
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Optional rate card"
                      options={(rateCardsQuery.data || []).map((c) => ({
                        value: c.grade_code,
                        label: `${c.grade_code} — ${c.grade_name}`,
                      }))}
                      onChange={(grade) => {
                        const card = (rateCardsQuery.data || []).find((c) => c.grade_code === grade);
                        if (card) {
                          operationForm.setFieldsValue({
                            labor_grade_code: card.grade_code,
                            labor_rate: Number(card.hourly_rate) || 0,
                            source_currency: card.currency || estimate.currency || 'AED',
                          });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="labor_rate" label={`Labor Rate (${estimate.currency || 'AED'}/hr)`} initialValue={0}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="machine_rate" label={`Machine Rate (${estimate.currency || 'AED'}/hr)`} initialValue={0}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="tooling_cost" label={`Tooling / Material Cost (${estimate.currency || 'AED'})`} initialValue={0}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="crew_size" label="Crew size" initialValue={1}>
                    <InputNumber min={0.0001} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="productivity_units_per_hour" label="Units / hour">
                    <InputNumber min={0} precision={4} style={{ width: '100%' }} placeholder="Optional" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="source_currency" label="Source currency" initialValue={estimate.currency || 'AED'} hidden>
                <Input />
              </Form.Item>
              <Form.Item name="override_reason" label="Override reason" help="Required only when manually overriding a master/rate-card labor rate.">
                <Input placeholder="Optional" />
              </Form.Item>
            </>
          )}

          <Form.Item name="remarks" label="Notes">
            <Input.TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>
        </Form>
      </Modal>

      <QuickCreateOperationModal
        open={quickCreateOperationOpen}
        presetName={operationSelectSearch}
        onCancel={() => setQuickCreateOperationOpen(false)}
        onCreated={(op) => {
          queryClient.setQueryData(
            ['manufacturing-operations-master', 'assembly-estimate'],
            (old: MasterOperation[] | undefined) => {
              const list = old || [];
              if (list.some((x) => x.id === op.id)) return list;
              return [...list, op];
            }
          );
          applyMasterOperation(op, operationForm);
          setOperationSelectSearch('');
        }}
      />

      <Modal
        title="Edit material"
        open={Boolean(editingMaterial)}
        onCancel={() => setEditingMaterial(null)}
        onOk={() =>
          editMaterialForm.validateFields().then(async (v) => {
            if (!editingMaterial) return;
            try {
              const res = await assemblyEstimateService.updateMaterial(estimate.id, editingMaterial.id, {
                ...v,
                rate: Number(v.rate ?? 0),
                source_rate: Number(v.rate ?? 0),
                is_override: Boolean(v.override_reason),
              });
              message.success('Material updated');
              setEditingMaterial(null);
              if (!applyDetailResponse(res)) await reload();
            } catch (error: any) {
              message.error(error?.response?.data?.message || error?.message || 'Update failed');
              return Promise.reject(error);
            }
          })
        }
        destroyOnClose
      >
        <Form form={editMaterialForm} layout="vertical">
          <Form.Item name="description" label="Description" rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={8}>
            <Col span={8}><Form.Item name="net_qty_per_unit" label="Qty"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="yield_percent" label="Yield %"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="scrap_percent" label="Scrap %"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="scrap_credit_rate" label="Scrap credit"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="uom" label="UOM"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="source_currency" label="Currency"><Input /></Form.Item></Col>
            <Col span={8}><Form.Item name="rate" label="Rate"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={16}><Form.Item name="override_reason" label="Override reason"><Input /></Form.Item></Col>
            <Col span={8}>
              <Form.Item name="customer_supplied" label="Supply">
                <Select options={[{ value: false, label: 'Costed' }, { value: true, label: 'Customer supplied' }]} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={editingIndirect ? 'Edit project indirect' : 'Add project indirect'}
        open={indirectModalOpen}
        onCancel={() => {
          setIndirectModalOpen(false);
          setEditingIndirect(null);
        }}
        onOk={() =>
          submitIndirect().catch((error: any) => {
            message.error(error?.response?.data?.message || error?.message || 'Could not save indirect');
            return Promise.reject(error);
          })
        }
        okText={editingIndirect ? 'Save' : 'Add indirect'}
        destroyOnClose
        width={640}
      >
        <Form form={indirectForm} layout="vertical" preserve={false}>
          <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Category is required' }]}>
            <AutoComplete
              options={INDIRECT_CATEGORY_SUGGESTIONS.map((c) => ({ value: c }))}
              placeholder="e.g. Electricity, Site facilities"
              filterOption={(input, option) =>
                String(option?.value || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="scope" label="Scope" initialValue="ESTIMATE" rules={[{ required: true }]}>
                <Select options={INDIRECT_SCOPE_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="method" label="Calculation method" initialValue="FIXED" rules={[{ required: true }]}>
                <Select options={INDIRECT_METHOD_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          {indirectScopeWatch === 'TOP_LEVEL' ? (
            <Form.Item
              name="node_id"
              label="Top-level assembly"
              rules={[{ required: true, message: 'Select the assembly this indirect applies to' }]}
            >
              <Select
                placeholder="Quote assembly"
                options={quoteNodes.map((n) => ({ value: n.id, label: n.node_name }))}
              />
            </Form.Item>
          ) : null}
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={indirectMethodHint(indirectMethodWatch)} />
          {(indirectMethodWatch === 'FIXED' || indirectMethodWatch === 'MANUAL') && (
            <Form.Item name="fixed_amount" label="Amount" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          )}
          {indirectMethodWatch === 'PERCENT_DIRECT' && (
            <Form.Item name="percent_value" label="Percent of direct" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          )}
          {indirectMethodWatch === 'QTY_RATE' && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="rate" label="Rate" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
            </Row>
          )}
          {indirectMethodWatch === 'DURATION_RATE' && (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="duration_quantity"
                  label="Duration"
                  tooltip={`Estimate duration is ${Number(estimate.duration_quantity || 0)}. Leave blank to use that.`}
                >
                  <InputNumber style={{ width: '100%' }} min={0} placeholder={`Default ${Number(estimate.duration_quantity || 0)}`} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="rate" label="Rate / duration unit" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} placeholder="Optional note" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingEscalation ? 'Edit escalation' : 'Add escalation'}
        open={escalationModalOpen}
        onCancel={() => {
          setEscalationModalOpen(false);
          setEditingEscalation(null);
        }}
        onOk={() =>
          submitEscalation().catch((error: any) => {
            message.error(error?.response?.data?.message || error?.message || 'Could not save escalation');
            return Promise.reject(error);
          })
        }
        okText={editingEscalation ? 'Save' : 'Add escalation'}
        destroyOnClose
        width={480}
      >
        <Form form={escalationForm} layout="vertical" preserve={false}>
          <Form.Item name="cost_category" label="Cost category" rules={[{ required: true }]}>
            <Select options={ESCALATION_CATEGORIES.map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="percent_value" label="Percent" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="period_label" label="Period label">
            <Input placeholder="e.g. 2027 materials" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingOffer ? 'Edit budgetary offer' : 'Add budgetary offer'}
        open={offerModalOpen}
        onCancel={() => {
          setOfferModalOpen(false);
          setEditingOffer(null);
          setOfferAttachment(null);
        }}
        onOk={() =>
          submitOffer().catch((error: any) => {
            message.error(error?.response?.data?.message || error?.message || 'Could not save offer');
            return Promise.reject(error);
          })
        }
        okText={editingOffer ? 'Save' : 'Add offer'}
        destroyOnClose
        width={640}
      >
        <Form form={offerForm} layout="vertical" preserve={false}>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="target_type" label="Line type" rules={[{ required: true }]} initialValue="MATERIAL">
                <Select
                  options={[
                    { value: 'MATERIAL', label: 'Material' },
                    { value: 'OPERATION', label: 'Operation' },
                  ]}
                  onChange={() => offerForm.setFieldValue('target_id', undefined)}
                />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="target_id" label="Cost line" rules={[{ required: true, message: 'Select a line' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={offerTargetType === 'OPERATION' ? 'Operation line' : 'Material line'}
                  options={offerTargetOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="supplier_id" label="Supplier">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select supplier"
              options={suppliers.map((s) => ({
                value: s.id,
                label: s.supplier_name || s.name || s.id,
              }))}
              onChange={(value) => {
                const supplier = suppliers.find((s) => s.id === value);
                offerForm.setFieldValue('supplier_name', supplier?.supplier_name || supplier?.name || '');
              }}
            />
          </Form.Item>
          <Form.Item name="supplier_name" hidden>
            <Input />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amount" label="Amount" rules={[{ required: true }]} initialValue={0}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currency" label="Currency" initialValue={estimate.currency || 'AED'}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label="Note">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          <Form.Item label="Attachment">
            <Upload
              showUploadList={!!offerAttachment}
              maxCount={1}
              beforeUpload={(file) => {
                setOfferAttachment(file);
                return false;
              }}
              onRemove={() => setOfferAttachment(null)}
            >
              <Button icon={<UploadOutlined />}>
                {offerAttachment
                  ? offerAttachment.name
                  : editingOffer?.attachment_document_id
                    ? 'Replace attachment'
                    : 'Attach quote'}
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingRateCard ? 'Edit labor rate card' : 'Add labor rate card'}
        open={rateCardModalOpen}
        onCancel={() => {
          setRateCardModalOpen(false);
          setEditingRateCard(null);
        }}
        onOk={() =>
          submitRateCard().catch((error: any) => {
            message.error(error?.response?.data?.message || error?.message || 'Could not save rate card');
            return Promise.reject(error);
          })
        }
        okText={editingRateCard ? 'Save' : 'Add rate card'}
        destroyOnClose
        width={480}
      >
        <Form form={rateCardForm} layout="vertical" preserve={false}>
          <Form.Item name="grade_code" label="Grade code" rules={[{ required: true }]}>
            <Input placeholder="e.g. SKILLED" disabled={Boolean(editingRateCard)} />
          </Form.Item>
          <Form.Item name="grade_name" label="Grade name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Skilled fitter" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="hourly_rate" label="Hourly rate" rules={[{ required: true }]} initialValue={0}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="currency" label="Currency" initialValue={estimate.currency || 'AED'}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Convert to quotation"
        open={convertOpen}
        onCancel={() => setConvertOpen(false)}
        onOk={() => {
          if (!convertSelectedIds.length) {
            message.warning('Select at least one assembly line');
            return;
          }
          return run(async () => {
            const res = await assemblyEstimateService.convert(estimate.id, convertSelectedIds);
            setConvertOpen(false);
            message.info(`Quotation ${res.data.quotation_number}`);
            navigate(`/growth/quotations/${res.data.quotation_id}`);
          }, 'Quotation created');
        }}
        okText="Convert selected"
      >
        {convertCandidates.length === 0 ? (
          <Alert type="info" message="No unquoted top-level assemblies available." />
        ) : (
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            value={convertSelectedIds}
            onChange={(values) => setConvertSelectedIds(values.map(String))}
            options={convertCandidates.map((n) => ({
              value: n.id,
              label: `${n.node_name} · ${money(n.selling_price)}`,
            }))}
          />
        )}
      </Modal>

      <Modal
        title="Customer summary"
        open={customerSummaryOpen}
        onCancel={() => setCustomerSummaryOpen(false)}
        footer={[
          <Upload
            key="upload"
            showUploadList={false}
            beforeUpload={async (file) => {
              await run(async () => {
                const doc = await documentService.uploadDocument(file, {
                  entity_type: 'assembly_estimate',
                  entity_id: estimate.id,
                  document_name: file.name,
                });
                await assemblyEstimateService.saveCustomerSummary(estimate.id, { document_id: doc.id });
              }, 'Attachment linked');
              return false;
            }}
          >
            <Button>Upload attachment</Button>
          </Upload>,
          <Button key="close" type="primary" onClick={() => setCustomerSummaryOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
          {customerSummaryText || estimate.customer_summary_text || 'No summary text.'}
        </Paragraph>
      </Modal>
    </div>
  );
};

export default AssemblyEstimateBuilderPage;
